import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PlanificadorService } from '../../models/control.service';
import { MatDialog } from '@angular/material/dialog';
import {
  createChart,
  CandlestickSeries,
  BarSeries,
  AreaSeries,
  BaselineSeries,
  LineSeries,
  HistogramSeries,
  createSeriesMarkers,
  ColorType,
  CrosshairMode,
  IChartApi,
} from 'lightweight-charts';
import { CompaniaWebsocketService } from '../../../services/compania-websocket.service';
import { global } from 'src/app/global';
import { ActivatedRoute } from '@angular/router';
import { TableColmuns } from 'src/app/interface/TableColumnsinterface';
import { CdkTableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { combineLatest, Subscription } from 'rxjs';
import { TemaService } from 'src/app/services/tema.services';
import { RastreadorService } from 'src/app/services/rastreador.service';
import { SseService } from 'src/app/services/SseService';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';
import { CookieService } from 'ngx-cookie-service';
import { ChartDrawingController } from '../../common/chart-drawing/chart-drawing-controller';
import { getToolLabel } from '../../common/chart-drawing/drawing-tools.catalog';
import { fitOverlayToPriceScale } from '../../common/chart-drawing/offscale-line';
import { BrokerTimeService, MarketSchedule } from 'src/app/services/broker-time.service';
import { DrawingTextDialogComponent } from '../../common/chart-drawing/drawing-text-dialog/drawing-text-dialog.component';
import { INDICATOR_CATALOG, IndCatalogEntry } from '../../common/chart-core/indicator-catalog.const';
import { OSCILLATORS } from '../../common/chart-core/oscillators.const';
import { getChartColors } from '../../common/chart-core/chart-colors';
import { PERIODO_TO_TIMEFRAME, RANGES, TIMEFRAME_TO_PERIODO, isPeriodoIntraday, rangeFromMs } from '../../common/chart-core/timeframes.const';
import { castEntryConditions, splitCondicionLegible } from '../../common/chart-core/cast-conditions';
import { OpenTradeLevels, applyOpenTradeLines } from '../../common/chart-core/open-trade-lines';
import { ChartSegment, SegmentLinesPrimitive, SegmentLinesStyle } from '../../common/chart-core/segment-lines-primitive';

interface DataObject {
  [key: string]: string;
}

interface dataAdcional {
  tipo: string;
  valor: string;
  fecha: number;
}

interface DescripcionRow extends dataAdcional {
  ind?: boolean;
  tiposalida?: string;
  valor1: string;
  valor2: string;
  valor3: string | number;
  valor4: string;
  isEntrada: boolean;
  isSalida: boolean;
  rowKey: string;
}

/** Opción del combo "Modelo" de la gráfica embebida (Portfolio Labs: una por cartera seleccionada). */
export interface ModeloGraficaOpcion {
  idmodelo: number;
  nombre: string;
  idmercado: number;
}

@Component({
  selector: 'app-grafica-modelo',
  templateUrl: './grafica-modelo.component.html',
  styleUrls: ['./grafica-modelo.component.scss'],
})
export class GraficaModeloComponent implements OnInit, OnDestroy {
  // Performance knobs
  private readonly INDICATOR_BATCH_SIZE = 3;
  private readonly INDICATOR_BATCH_DELAY = 0;
  private readonly INITIAL_WINDOW_DAYS = 180;
  /** Barras visibles en la vista inicial; INITIAL_WINDOW_DAYS_BY_TIMEFRAME debe cubrirlas en cada periodo */
  private readonly INITIAL_VIEW_BARS = 250;
  private readonly DAY_MS = 24 * 60 * 60 * 1000;

  /** Approximate milliseconds per bar for the current timeframe */
  private getBarMs(): number {
    const tf = this.activeTimeframe ?? PERIODO_TO_TIMEFRAME[this.modeloPeriodo] ?? 'D';
    switch (tf) {
      case '1m': return 60 * 1000;
      case '5m': return 5 * 60 * 1000;
      case '15m': return 15 * 60 * 1000;
      case '30m': return 30 * 60 * 1000;
      case '1H': return 60 * 60 * 1000;
      case '4H': return 4 * 60 * 60 * 1000;
      case 'D': return this.DAY_MS;
      case 'W': return 7 * this.DAY_MS;
      default: return this.DAY_MS;
    }
  }

  /**
   * Calcula `start`/`end` para el payload de cast_conditions:
   * - Si `existingStart` es una fecha válida (ej. condicionln.start de un modelo guardado) → se usa tal cual.
   * - Si no: "días" (no intraday) → 200 días atrás; intraday → 200 barras del timeframe activo.
   * Compartido entre la rama con modelo (parametrosmodelo) y la rama sin modelo (seguimiento
   * pasivo/selector/período) para que ambas manden un rango de fechas válido al backend.
   */
  private computeStartEnd(existingStart: any): { start: string; end: string } {
    const BAR_COUNT = 200;
    const endDateStr = new Date().toISOString().slice(0, 10);
    const isValidDateStr = typeof existingStart === 'string' && !isNaN(new Date(existingStart).getTime());
    let startDateStr: string;
    if (isValidDateStr) {
      startDateStr = existingStart;
    } else if (!this._isIntraday) {
      const d = new Date();
      d.setUTCDate(d.getUTCDate() - BAR_COUNT);
      startDateStr = d.toISOString().slice(0, 10);
    } else {
      const msPerBar = this.getBarMs();
      const startMs = Date.now() - BAR_COUNT * msPerBar;
      const d = new Date(startMs);
      startDateStr = d.toISOString().slice(0, 10);
    }
    return { start: startDateStr, end: endDateStr };
  }

    // Socket handler: do NOT update candles directly from socket.
    // Instead trigger a server-side `calculo/cast_conditions` call and merge returned OHLCV.
    public onSocketUpdate(data: any): void {
      this.companiaData = data;
      // Coalesce rapid socket updates into a single server call.
      try {
        if (this.castTimeout) clearTimeout(this.castTimeout);
      } catch (e) {}
      this.castTimeout = setTimeout(() => {
        try { this.triggerCastConditionsFromSocket(); } catch (e) { console.warn('triggerCastConditionsFromSocket failed', e); }
      }, this.castDebounceMs);
    }

    private triggerCastConditionsFromSocket(): void {
      // Avoid concurrent calls: if one is in-flight, mark pending and return.
      if (this.castInFlight) { this.castPendingFromSocket = true; return; }

      let payload: any = null;
      if (this.jsoncondicon && typeof this.jsoncondicon === 'object') payload = { ...this.jsoncondicon };
      else payload = {
        list_conditions_entry: this.jsoncondicon?.list_conditions_entry ?? [{ dsl: this.condicion }],
        list_conditions_exit: this.jsoncondicon?.list_conditions_exit ?? [],
        idnectums: [this.idnectum], periodo: this.modeloPeriodo, metaapi: this._fetchMetaapi ?? false,
      };
      try { delete payload.ln; } catch {}

      this.castInFlight = true;
      this._sseService.apiPostNew('calculo/cast_conditions', payload, '').subscribe({
        next: (resp: any) => {
          try {
            let indicadorsIa: any = resp;
            if (Array.isArray(resp)) {
              if (resp.length && resp[0] && resp[0].result !== undefined) {
                indicadorsIa = { result: resp.flatMap((r: any) => r.result || []) };
              } else {
                indicadorsIa = { result: resp };
              }
            }
            this.mergeCastToHistory(indicadorsIa);
            try { this.applyIndicadorsIaIncremental(indicadorsIa); } catch (e) { console.warn('applyIndicadorsIaIncremental failed', e); }
          } catch (e) { console.warn('cast_conditions resp handling failed', e); }
        },
        error: (err: any) => { console.warn('cast_conditions error (socket trigger)', err); },
        complete: () => {
          this.castInFlight = false;
          if (this.castPendingFromSocket) {
            this.castPendingFromSocket = false;
            setTimeout(() => this.triggerCastConditionsFromSocket(), 100);
          }
        }
      });
    }

    private mergeCastToHistory(indicadorsIa: any): void {
      try {
        if (!indicadorsIa || !Array.isArray(indicadorsIa.result) || !indicadorsIa.result.length) return;
        const rowsToInsert: number[][] = [];
        for (const item of indicadorsIa.result) {
          const fechaMs = this.parseFechaMs(item.fecha);
          if (!fechaMs || !Number.isFinite(fechaMs)) continue;
          const open = Number(item.open ?? item.Open ?? item.o ?? NaN);
          const high = Number(item.high ?? item.High ?? item.h ?? NaN);
          const low = Number(item.low ?? item.Low ?? item.l ?? NaN);
          const close = Number(item.close ?? item.Close ?? item.c ?? NaN);
          const vol = Number(item.volume ?? item.vol ?? item.Volume ?? 0);
          if (![open, high, low, close].every(v => Number.isFinite(v))) continue;
          rowsToInsert.push([fechaMs, open, high, low, close, vol]);
        }
        if (!rowsToInsert.length) return;

        const existingMap = new Map<number, number>();
        for (let i = 0; i < this.fullPriceHistory.length; i++) existingMap.set(Number(this.fullPriceHistory[i][0]), i);
        for (const row of rowsToInsert) {
          const ts = Number(row[0]);
          if (existingMap.has(ts)) {
            const idx = existingMap.get(ts)!;
            this.fullPriceHistory[idx][1] = row[1];
            this.fullPriceHistory[idx][2] = row[2];
            this.fullPriceHistory[idx][3] = row[3];
            this.fullPriceHistory[idx][4] = row[4];
            this.fullPriceHistory[idx][this.fullPriceHistory[idx].length - 1] = row[5];
          } else {
            this.fullPriceHistory.push(row);
          }
        }

        this.fullPriceHistory.sort((a: any, b: any) => Number(a[0]) - Number(b[0]));
        this.fullVolumeHistory = this.fullPriceHistory.map((r: any[]) => [r[0], r[r.length - 1]] as [number, number | null]);
        try { this._chartOhlcCache = this.convertToLwcOhlc(this.fullPriceHistory); } catch (e) { console.warn('convertToLwcOhlc failed', e); }
        this.queueFullHistoryMerge();
      } catch (e) {
        console.warn('mergeCastToHistory failed', e);
      }
    }

  // Raw history (loaded window so far, ms timestamps) for lazy-merge
  private fullPriceHistory: number[][] = [];
  private fullVolumeHistory: Array<[number, number | null]> = [];
  private pendingHistoryTimer: any = null;

  // ── Chunked history loading (Fase 3/4) ────────────────────────────────────
  /** Oldest ms timestamp currently requested/loaded for candles */
  private loadedFrom: number | null = null;
  /** Newest ms timestamp currently requested/loaded for candles */
  private loadedTo: number | null = null;
  private noMoreOlderHistory = false;
  private noMoreNewerHistory = false;
  /** Chunks vacíos consecutivos: un hueco en la BD (feriados, días sin 1m) no es el fin del histórico */
  private olderEmptyStreak = 0;
  private newerEmptyStreak = 0;
  /** Un salto grande volvió vacío: pedir chunks normales hasta recibir velas */
  private olderForceChunk = false;
  private newerForceChunk = false;
  /** Fecha más nueva observada en los datos (indicadores/ticks); techo de fetchNewerChunk junto con Date.now() */
  private latestKnownMs = 0;
  private olderChunkInFlight = false;
  private newerChunkInFlight = false;
  /** Guards fullPriceHistory + candleSeries.setData against concurrent socket-merge / older-chunk mutations */
  private historyMutationLock = false;
  private visibleRangeHandler: ((range: any) => void) | null = null;

  // ── Range buttons (1M/3M/.../All) — multi-chunk load-then-navigate (ver setZoomRange) ──
  /** true mientras setZoomRange() está trayendo chunks antes de aplicar el rango pedido */
  public rangeLoading = false;
  /** Incrementado en cada fetchData()/setZoomRange() nuevo para invalidar loops de setZoomRange en curso */
  private _rangeRequestSeq = 0;
  /** true si se llegó al principio real del histórico (o al tope intradía) — para el tooltip "no hay más historial" */
  public get noMoreOlderHistoryReached(): boolean { return this.noMoreOlderHistory; }

  // ── lightweight-charts instances ──────────────────────────────────────────
  private lwChart: IChartApi | null = null;
  private candleSeries: any = null;
  private volumeSeries: any = null;
  private lastVolumeTime: any = null;
  /** Conectores entrada→salida de todas las operaciones, en un solo primitive sobre candleSeries
   * (ver rebuildOperLineSeries). */
  private operLines: SegmentLinesPrimitive | null = null;
  /** Líneas PI→PF de los períodos de "Análisis Periodos" (modo embebido, ver rebuildPeriodoLineSeries). */
  private periodoLines: SegmentLinesPrimitive | null = null;
  private markersPlugin: any = null;
  /** SL/TP de la operación abierta (ver computeOpenTradeLevels) y sus price lines sobre candleSeries. */
  private openTradeLevels: OpenTradeLevels | null = null;
  private openTradePriceLines: any[] = [];
  /** Map key → ISeriesApi for indicator lines */
  private indicatorSeriesMap: Map<string, any> = new Map();
  /** Pane used for oscillator-type indicators */
  private oscillatorPane: any = null;
  /** Stored markers array for re-use (entradas + salidas + fecha highlight) */
  private allMarkers: any[] = [];
  /**
   * Referencia propia del componente al div del chart, en vez de document.getElementById('container')
   * (lookup global). Necesario porque, en modo embebido dentro de un mat-tab-group, la pestaña
   * inactiva queda "detached" del árbol del DOM (Angular Material desconecta el contenido, no lo
   * oculta con display:none) — el componente sigue vivo y sus suscripciones siguen disparando, pero
   * getElementById ya no encuentra el nodo. @ViewChild sí sigue resolviendo el elemento porque
   * apunta a la vista propia del componente, exista o no actualmente en el documento.
   */
  @ViewChild('container') private containerRef?: ElementRef<HTMLDivElement>;

  /** Referencia al div raíz .container-fluid, usada en modo embebido por syncEmbeddedContainerHeight()
   * para fijar su altura real vía JS en vez de adivinar el offset de chrome superior en CSS. */
  @ViewChild('containerFluid') private containerFluidRef?: ElementRef<HTMLDivElement>;

  /** Legend items: active indicator name + color + value at crosshair (lastValue = last bar, shown when mouse leaves) */
  public legendItems: Array<{id: string; name: string; color: string; value: string; lastValue: string}> = [];
  /** OHLC values shown on crosshair hover */
  public crosshairOhlc: { open: number|null; high: number|null; low: number|null; close: number|null; change: number|null; changePct: number|null } | null = null;
  private crosshairHandler: ((params: any) => void) | null = null;
  private _chartOhlcCache: any[] = [];
  /** Extra candlestick series for additional assets */
  private extraCandleSeries: Map<number, any> = new Map();
  /** Line series drawn for chart pattern geometry */
  private patternSeriesList: any[] = [];
  /** ResizeObserver for container */
  private resizeObserver: ResizeObserver | null = null;

  // ── Theme ─────────────────────────────────────────────────────────────────
  theme: string | string[] | undefined;
  backgroundColor: string = '#ffffff';
  color: string = '#000000';
  public isDark: boolean = false;

  // ── Colors ────────────────────────────────────────────────────────────────
  private readonly colores: string[] = [
    '#FF5733', '#33FF57', '#3357FF', '#F1C40F', '#8E44AD',
    '#FF33A8', '#33FFF5', '#FF8C33', '#A833FF', '#33FF8C',
    '#FF3333', '#FFB833', '#33B8FF', '#B8FF33', '#FF33B3',
    '#B3FF33', '#FF33B8', '#33B3FF', '#B8A833', '#A833B8',
  ];
  private colorIndex = 0;

  // ── Embedded mode (incrustado dentro de Models, sin ruta propia) ───────────
  /** Cuando es true, el componente ignora ActivatedRoute y sigue el análisis/modelo activo de RastreadorService/PlanificadorService. Por defecto false: no afecta el uso actual por ruta (`/grafica-modelo/:data`). */
  @Input() embedded: boolean = false;
  /**
   * Modelos entre los que elegir en el modo embebido (Portfolio Labs: uno por cartera seleccionada).
   * Models no lo pasa (un solo modelo vía ControlService.idModeloActivo) y queda vacío: todo lo que
   * depende de esta lista se salta cuando está vacía.
   */
  @Input() set modelos(v: ModeloGraficaOpcion[] | null) {
    this.modelosLista = (v ?? []).filter(m => Number(m?.idmodelo) > 0);
    // Sin guardia por `embedded`: solo lo pasa Portfolio Labs (siempre embebido) y no depender del
    // orden en que Angular asigna los inputs.
    if (!this.modelosLista.length) return;
    // Cartera del modelo mostrado deseleccionada: lo que hay en la gráfica ya no pertenece a la selección.
    if (Number(this.idmodelo) > 0 && !this.modelosLista.some(m => m.idmodelo === Number(this.idmodelo))) {
      this.limpiarGrafica();
    }
    if (!this.modelosLista.some(m => m.idmodelo === this.selectedModeloId)) {
      this.seleccionarModelo(this.modelosLista[0]);
    }
  }
  public modelosLista: ModeloGraficaOpcion[] = [];
  /** Modelo elegido en el combo "Modelo" (solo Portfolio Labs); se mantiene en sync con cargarActivo(). */
  public selectedModeloId: number | null = null;
  private subAnalisisCompania: Subscription;
  private subPeriodosCompania: Subscription;
  private subPeriodosEnriquecidos: Subscription;
  private subPeriodoEnfocar: Subscription;
  private subModeloSeleccionadoGrafica: Subscription;
  private subIdMercado: Subscription;
  private subIdModeloActivo: Subscription;
  /** Período de "Análisis Periodos" pendiente de enfocar (zoom) cuando el chart esté listo. */
  private _pendingFocusPeriodo: { inicioMs: number; finMs: number } | null = null;
  private _focusPeriodoTimer: any = null;

  /** Nº de cargas de velas en curso (fetch inicial + chunks hacia atrás). Alimenta el overlay "Cargando velas…". */
  private _cargasVelasEnCurso = 0;
  public get cargandoVelas(): boolean { return this._cargasVelasEnCurso > 0; }
  private beginCargaVelas(): void { this._cargasVelasEnCurso++; this.cdr.markForCheck(); }
  private endCargaVelas(): void {
    this._cargasVelasEnCurso = Math.max(0, this._cargasVelasEnCurso - 1);
    this.cdr.markForCheck();
  }
  /** Períodos de "Análisis Periodos" de la compañía activa (modo embebido), marcados sobre el chart. */
  private periodosActivos: { inicioMs: number; finMs: number; valorInicio: number; valorFin: number }[] = [];
  /** Filas enriquecidas (con volatilidad) de "Análisis Periodos" + inicioMs/finMs, fuente de la leyenda por hover. */
  private periodosLegendSource: any[] = [];
  /** Período bajo el cursor (crosshair) en modo embebido — alimenta la leyenda sobre el chart. */
  public hoveredPeriodoLegend: any | null = null;

  // ── Component state ───────────────────────────────────────────────────────
  companiaData: any;
  private subscription: Subscription;
  public data: any;
  public expandir: boolean = false;
  public dataAdicional = new CdkTableVirtualScrollDataSource<DescripcionRow>();
  public descripcionRowsCount: number = 0;
  public filaSeleccionada: DescripcionRow | null = null;
  public dataGra: any;
  public isModelo: boolean = false;
  public subDarktema: Subscription;
  nombreCompania: string;
  idmercado: number = 0;
  public strcondicion: string;
  idActivo: number = 0;
  activoExtra: number[] = [];
  idmodelo: any;
  condicion: string;
  condicionEntradaLista: string[] = [];
  condicionSalidaLista: string[] = [];

  jsoncondicon = JSON.parse(
    '{"list_conditions_entry":[{"indicator":"CLOSE: Close","operador":"sum","n_operador":0,"period":0,"asset_name":0,"day_offset":0,"logic_operator":">","const":240}],"list_conditions_exit":[{"indicator":"CLOSE: Close","operador":"sum","n_operador":0,"period":0,"asset_name":0,"day_offset":0,"logic_operator":">","const":500}],"start":"2024-12-26","end":"2024-12-30","idnectums":[],"stopLoss":20,"bajista":false,"periodo":6,"idmercado":8,"ln":[["CLOSE:_Close > 240","Entrada",{"indicator":"CLOSE: Close","period":0,"asset_name":0,"const":240,"day_offset":0,"n_operador":0,"logic_operator":">","operador":"sum"}],["CLOSE:_Close > 500","Salida",{"indicator":"CLOSE: Close","period":0,"asset_name":0,"const":500,"day_offset":0,"n_operador":0,"logic_operator":">","operador":"sum"}]]}'
  );
  jsoncomponentesprecio = ['price:_close', 'price:_high', 'price:_low', 'price:_open'];
  idnectum: number = 0;
  rsi = false; macd = false; ema = false; mom = false; sma = false; mh = false;
  public companias: any;
  private _idmercadoCompaniasCargado: number | null = null;
  /** Activo elegido en el selector embebido (ver Input `embedded`); se mantiene en sync con cargarActivo(). */
  public selectedCompanyId: number | null = null;

  // ── Market countdown ──────────────────────────────────────────────────────
  marketCountdownText: string = '';
  marketDisplayName: string = '';
  marketTimezone: string = '';
  isMarketOpen: boolean = false;
  private marketCountdownTimer: any = null;
  private marketSchedule: MarketSchedule | null = null;

  // ── Oscillators list ──────────────────────────────────────────────────────
  oscillators: string[] = OSCILLATORS;
  listPuntps: string[] = ['RSI'];
  public salidas: any[] = [];
  public entradas: any[] = [];
  public operaciones: any[] = [];
  groupedIndicators: { [key: string]: {}[] } = {};
  private castTimeout: any = null;
  private castDebounceMs: number = 30000; // debounce before re-casting conditions
  private castInFlight: boolean = false;
  private castPendingFromSocket: boolean = false;

  // ── Top bar state ────────────────────────────────────────────────────────
  public chartType: string = 'candlestick';
  public activeTimeframe: string = 'D';
  public activeRange: string = '';
  public showIndicatorsPanel: boolean = false;
  public showCandleDropdown: boolean = false;
  public indicatorVisibility: Map<string, boolean> = new Map();
  public indicatorSearch: string = '';

  @HostListener('document:click')
  onDocumentClick() { this.showCandleDropdown = false; }

  /** Reflows can shift how much chrome sits above .container-fluid (p. ej. el header de Models
   * envolviendo texto en una pantalla angosta) — re-sincronizar en cada resize de ventana. */
  @HostListener('window:resize')
  onWindowResize() { this.refreshLayout(); }

  // ── Indicator catalog (client-side, from lightweight-charts-indicators) ─
  public readonly indicatorCatalog: IndCatalogEntry[] = INDICATOR_CATALOG;

  public readonly timeframes: string[] = ['1m', '5m', '15m', '30m', '1H', '4H', 'D', 'W', 'M'];
  public readonly ranges: string[] = RANGES;
  /**
   * Ventana inicial de velas (y tamaño de cada chunk al hacer scroll hacia atrás) por
   * timeframe. 180 días de 1m son ~185k velas (~10MB) — para timeframes de minutos hace
   * falta una ventana mucho más chica para un primer pintado rápido; el resto se carga
   * bajo demanda (Fase 4). Cada ventana debe contener al menos INITIAL_VIEW_BARS (250) velas:
   * 'D' necesita ~1 año (250 sesiones) y 'W' ~5 años (250 semanas); '4H' cabe en 180 días.
   */
  private readonly INITIAL_WINDOW_DAYS_BY_TIMEFRAME: Record<string, number> = {
    '1m': 10, '5m': 30, '15m': 60, '30m': 90, '1H': 120, 'D': 400, 'W': 1850,
  };
  private getInitialWindowDays(): number {
    return this.INITIAL_WINDOW_DAYS_BY_TIMEFRAME[this.activeTimeframe] ?? this.INITIAL_WINDOW_DAYS;
  }
  public modeloPeriodo: number = 6;
  // Indica si los controles de periodo/timeframe deben estar bloqueados (modelo cargado)
  public get periodoBloqueado(): boolean {
    const id = Number(this._fetchIdModelo ?? this.idmodelo ?? 0);
    return Number.isFinite(id) && id > 0;
  }
  private _isIntraday: boolean = false;
  private _fetchCompania: string = '';
  private _fetchMetaapi: boolean = false;
  private _fetchIdModelo: any = null;
  private _fetchIdNectum: any = null;
  private _fetchFecha: string | null = null;

  public get chartTypeIcon(): string {
    const icons: Record<string, string> = {
      'candlestick': 'bi bi-graph-up',
      'bar': 'bi bi-bar-chart',
      'line': 'bi bi-graph-up-arrow',
      'area': 'bi bi-triangle-fill',
      'baseline': 'bi bi-subtract',
      'heikin-ashi': 'bi bi-columns-gap',
    };
    return icons[this.chartType] || 'bi bi-graph-up';
  }

  private scheduleCastConditions(): void {
  if (this.castInFlight) return;
  if (!this.jsoncondicon) return;
  // NUEVO: no recalcular si el chart no está listo o no hay indicadores IA
  if (!this.lwChart || !this.candleSeries) return;
  if (this.indicatorSeriesMap.size === 0) return;

  if (this.castTimeout) clearTimeout(this.castTimeout);
  this.castTimeout = setTimeout(() => {
    this.castTimeout = null;
    this.castInFlight = true;
    try {
      const payload = this.jsoncondicon ?? null;
      if (!payload) { this.castInFlight = false; return; }
      this._sseService.apiPostNew('calculo/cast_conditions', payload, '').subscribe({
        next: (indicadorsIa: any) => {
          try { this.applyIndicadorsIaIncremental(indicadorsIa); }
          catch (e) { console.error('applyIndicadorsIaIncremental error', e); }
        },
        error: (err: any) => { console.warn('cast_conditions error', err); },
        complete: () => { this.castInFlight = false; }
      });
    } catch (e) {
      console.warn('cast_conditions schedule failed', e);
      this.castInFlight = false;
    }
  }, this.castDebounceMs);
}

  private applyIndicadorsIaIncremental(indicadorsIa: any): void {
    if (!indicadorsIa || !Array.isArray(indicadorsIa.result)) return;
    // Build groupedIndicators similarly to createCharts but without recreating chart
    const keys = Object.keys(indicadorsIa.result[0] ?? {});
    // Excluir campos no representables como series (OHLC, volumen, DSL y decisiones)
    const keysToRemove = [
      'close', 'low', 'high', 'open', 'volume', 'fecha', 'dsl_0',
      'entry_decision', 'exit_decision', 'entry_decision_final', 'exit_decision_final', 'entry_decision_0', 'exit_decision_1', 'entry_decision_2'
    ];
    const ketss = keys.filter((k) => !keysToRemove.includes(k) && !k.startsWith('pattern_'));

    // Reset groupedIndicators for these keys
    for (const key of ketss) { this.groupedIndicators[key] = []; }

    indicadorsIa.result.forEach((item: any) => {
      const unixFecha = Date.parse(item.fecha);
      ketss.forEach((key) => {
        let valor = item[key];
        if (typeof valor === 'string' && valor.toUpperCase() === 'NAN') valor = null;
        else if (typeof valor === 'number' && !Number.isFinite(valor)) valor = null;
        this.groupedIndicators[key].push({ unixFecha, valor });
      });
    });

    // Now update or create series for each indicator
    ketss.forEach((key) => {
      const lineData = this.convertToLwcLine(this.groupedIndicators[key] as any[]);
      const seriesKey = key.split(':')[0];
      const existing = this.indicatorSeriesMap.get(key);
      const lastValue = this.formatLegendValue(lineData[lineData.length - 1]?.value);
      try {
        if (existing) {
          existing.setData(lineData);
          const item = this.legendItems.find(l => l.id === key);
          if (item) { item.value = lastValue; item.lastValue = lastValue; }
        } else if (this.lwChart) {
          const c = this.getChartColors();
          const s = this.lwChart.addSeries(LineSeries, {
            color: c.text, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
          });
          s.setData(lineData);
          this.indicatorSeriesMap.set(key, s);
          this.legendItems.push({ id: key, name: key, color: c.text, value: lastValue, lastValue });
        }
      } catch (e) { console.warn('updating indicator series failed', key, e); }
    });
    // Series nuevas pueden ampliar el eje de tiempo: reindexar los marcadores (ver addIndicatorSeriesInBatches).
    this.applyMarkers();
    this.cdr.markForCheck();
  }

  /**
   * Tope de chunks-más-antiguos que setZoomRange('All') va a traer antes de fitContent().
   * Diario/semanal/mensual: prácticamente sin límite (el volumen de velas es chico).
   * Intradía: tope en días (no en cantidad fija de chunks, que varían de tamaño por timeframe —
   * ver INITIAL_WINDOW_DAYS_BY_TIMEFRAME) para no descargar más de ~2 años de velas de minutos,
   * lo que podría ser cientos de miles de barras. El resto sigue disponible haciendo scroll manual.
   */
  private getMaxOlderChunksForAll(): number {
    if (!this._isIntraday) return 200;
    const CAP_INTRADAY_DAYS = 730;
    return Math.max(1, Math.ceil(CAP_INTRADAY_DAYS / this.getInitialWindowDays()));
  }

  public async setZoomRange(range: string): Promise<void> {
    if (!this.lwChart || this.rangeLoading) return;
    this.activeRange = range;
    const mySeq = ++this._rangeRequestSeq;
    const company = this._fetchCompania;
    const isStale = () => mySeq !== this._rangeRequestSeq || this._fetchCompania !== company || !this.lwChart;

    this.rangeLoading = true;
    this.cdr.markForCheck();
    try {
      if (range === 'All') {
        const maxChunks = this.getMaxOlderChunksForAll();
        let guard = 0;
        while (!this.noMoreOlderHistory && guard++ < maxChunks) {
          if (isStale()) return;
          await this.fetchOlderChunk();
        }
        guard = 0;
        while (!this.noMoreNewerHistory && guard++ < maxChunks) {
          if (isStale()) return;
          await this.fetchNewerChunk();
        }
        if (isStale()) return;
        try { this.lwChart.timeScale().fitContent(); } catch {}
        return;
      }

      const now = new Date();
      const fromMs = rangeFromMs(range, now.getTime());

      const maxChunks = this.getMaxOlderChunksForAll();
      let guard = 0;
      while (this.loadedFrom != null && fromMs < this.loadedFrom && !this.noMoreOlderHistory && guard++ < maxChunks) {
        if (isStale()) return;
        await this.fetchOlderChunk();
      }
      if (isStale()) return;
      try {
        if (this._isIntraday) {
          this.lwChart.timeScale().setVisibleRange({
            from: Math.round(fromMs / 1000) as any,
            to: Math.round(now.getTime() / 1000) as any,
          });
        } else {
          this.lwChart.timeScale().setVisibleRange({
            from: this.toISODate(fromMs) as any,
            to: this.toISODate(now.getTime()) as any,
          });
        }
      } catch {}
    } finally {
      this.rangeLoading = false;
      this.cdr.markForCheck();
    }
  }

  public setTimeframe(tf: string): void {
    // No permitir cambio de timeframe si hay un modelo cargado
    if (this.periodoBloqueado) return;

    this.activeTimeframe = tf;
    const periodo = TIMEFRAME_TO_PERIODO[tf] ?? 6;
    this._isIntraday = isPeriodoIntraday(periodo);
    if (this._fetchCompania) {
      this.fetchData(this._fetchCompania, this._fetchIdModelo, this._fetchIdNectum, this._fetchFecha, periodo);
    }
  }

  public setChartType(type: string): void {
    if (!this.lwChart) return;
    this.chartType = type;
    const c = this.getChartColors();
    const ohlcData = this.convertToLwcOhlc(this.fullPriceHistory);
    const lineData = ohlcData.map((d: any) => ({ time: d.time, value: d.close }));

    if (this.candleSeries) {
      try { this.lwChart.removeSeries(this.candleSeries); } catch {}
      this.candleSeries = null;
    }
    if (this.markersPlugin) {
      try { this.markersPlugin.setMarkers([]); } catch {}
      this.markersPlugin = null;
    }

    switch (type) {
      case 'bar':
        this.candleSeries = this.lwChart.addSeries(BarSeries, {
          upColor: c.upColor, downColor: c.downColor,
        });
        this.candleSeries.setData(ohlcData);
        break;
      case 'line':
        this.candleSeries = this.lwChart.addSeries(LineSeries, {
          color: c.upColor, lineWidth: 2,
          priceLineVisible: false, lastValueVisible: true,
        });
        this.candleSeries.setData(lineData);
        break;
      case 'area':
        this.candleSeries = this.lwChart.addSeries(AreaSeries, {
          lineColor: c.upColor,
          topColor: c.upColor + '66',
          bottomColor: c.upColor + '11',
        });
        this.candleSeries.setData(lineData);
        break;
      case 'baseline':
        this.candleSeries = this.lwChart.addSeries(BaselineSeries, {});
        this.candleSeries.setData(lineData);
        break;
      case 'heikin-ashi':
        this.candleSeries = this.lwChart.addSeries(CandlestickSeries, {
          upColor: c.upColor, downColor: c.downColor,
          borderVisible: false, wickUpColor: c.upColor, wickDownColor: c.downColor,
        });
        this.candleSeries.setData(this.computeHeikinAshi(ohlcData));
        break;
      default:
        this.candleSeries = this.lwChart.addSeries(CandlestickSeries, {
          upColor: c.upColor, downColor: c.downColor,
          borderVisible: false, wickUpColor: c.upColor, wickDownColor: c.downColor,
        });
        this.candleSeries.setData(ohlcData);
    }

    this.applyOpenTradeLines(); // antes que los marcadores (ver createCharts)
    this.applyMarkers();
    this.rebuildOperLineSeries();
    this.rebuildPeriodoLineSeries();
    // La serie se recreó: reenganchar los dibujos a la nueva serie
    if (this.candleSeries) this.drawingCtl.reattachSeries(this.candleSeries);
  }

  private computeHeikinAshi(ohlc: any[]): any[] {
    const ha: any[] = [];
    for (let i = 0; i < ohlc.length; i++) {
      const curr = ohlc[i];
      const prev = i > 0 ? ha[i - 1] : curr;
      const haClose = (curr.open + curr.high + curr.low + curr.close) / 4;
      const haOpen = (prev.open + prev.close) / 2;
      ha.push({
        time: curr.time,
        open: haOpen,
        high: Math.max(curr.high, haOpen, haClose),
        low: Math.min(curr.low, haOpen, haClose),
        close: haClose,
      });
    }
    return ha;
  }

  public toggleIndicatorsPanel(): void {
    this.showIndicatorsPanel = !this.showIndicatorsPanel;
  }

  public get filteredAvailableIndicators(): IndCatalogEntry[] {
    const search = this.indicatorSearch.trim().toLowerCase();
    if (!search) return this.indicatorCatalog;
    return this.indicatorCatalog.filter(e =>
      e.title.toLowerCase().includes(search) || e.short.toLowerCase().includes(search)
    );
  }

  public isIndicatorInChart(id: string): boolean {
    return this.indicatorSeriesMap.has(id);
  }

  public isAddingIndicator(id: string): boolean {
    return this._addingSet.has(id);
  }

  private _addingSet = new Set<string>();

  /** Convert fullPriceHistory (ms timestamps) → Bar[] (unix seconds) for indicator functions */
  private toBars(): any[] {
    return this.fullPriceHistory.map(row => ({
      time: Math.floor(Number(row[0]) / 1000),
      open: Number(row[1]),
      high: Number(row[2]),
      low: Number(row[3]),
      close: Number(row[4]),
      volume: row[5] != null ? Number(row[5]) : undefined,
    }));
  }

  /** Convert TimeValue[] (unix seconds) → lightweight-charts data */
  private toChartData(tvArr: any[]): Array<{ time: any; value: number }> {
    return tvArr
      .filter(d => d != null && Number.isFinite(d.value))
      .map(d => ({
        time: this.toChartTime(Number(d.time) * 1000),
        value: d.value,
      }));
  }

  public addIndicatorToChart(entry: IndCatalogEntry): void {
    if (!this.lwChart || this._addingSet.has(entry.id)) return;
    this._addingSet.add(entry.id);
    this.cdr.markForCheck();

    try {
      const bars = this.toBars();
      if (!bars.length) return;

      const result = entry.fn(bars);

      // Normalize to ordered list of { label, data }
      const plotEntries: Array<{ label: string; data: any[] }> = [];

      if (result && typeof result === 'object' && !Array.isArray(result) && result.plots) {
        const metaPlots: any[] = result.metadata?.plots ?? [];
        if (metaPlots.length) {
          // Use metadata.plots for proper ordering and human-readable labels
          for (const pm of metaPlots) {
            const data = result.plots[pm.varName];
            if (Array.isArray(data) && data.length) {
              plotEntries.push({ label: pm.title || pm.varName, data });
            }
          }
        } else {
          // Fallback: take only the first plot key to avoid unnamed plot0/plot1 spam
          const firstKey = Object.keys(result.plots)[0];
          if (firstKey && result.plots[firstKey]?.length) {
            plotEntries.push({ label: entry.short, data: result.plots[firstKey] });
          }
        }
      } else if (Array.isArray(result)) {
        plotEntries.push({ label: entry.short, data: result });
      }

      if (!plotEntries.length) return;

      for (let i = 0; i < plotEntries.length; i++) {
        const { label, data } = plotEntries[i];
        const lineData = this.toChartData(data);
        if (!lineData.length) continue;

        const color = this.colores[this.colorIndex % this.colores.length];
        this.colorIndex++;
        // seriesId: primary plot uses entry.id, secondary plots use entry.id_0, entry.id_1 ...
        const seriesId = i === 0 ? entry.id : `${entry.id}_${i}`;
        const title = i === 0 ? entry.short : `${entry.short} · ${label}`;

        let series: any;
        if (!entry.overlay) {
          if (!this.oscillatorPane) this.oscillatorPane = (this.lwChart as any).addPane();
          series = this.oscillatorPane.addSeries(LineSeries, {
            color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
          });
        } else {
          series = this.lwChart!.addSeries(LineSeries, {
            color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
            priceFormat: { type: 'price', precision: 5, minMove: 0.00001 },
          });
        }
        series.setData(lineData);
        this.indicatorSeriesMap.set(seriesId, series);
        this.indicatorVisibility.set(seriesId, true);
        const lastValue = this.formatLegendValue(lineData[lineData.length - 1]?.value);
        this.legendItems.push({ id: seriesId, name: title, color, value: lastValue, lastValue });
      }
    } catch (e) {
      console.error('Error adding indicator:', entry.id, e);
    } finally {
      this._addingSet.delete(entry.id);
      this.cdr.markForCheck();
    }
  }

  private formatLegendValue(value: number | undefined): string {
    if (value == null || !Number.isFinite(value)) return '';
    return Math.abs(value) < 10 ? value.toFixed(4) : value.toFixed(2);
  }

  /** Sync legend values with the bar under the crosshair; restore last-bar values when the mouse leaves the chart */
  private updateLegendValuesAtCrosshair(params: any): void {
    let changed = false;
    if (!params?.time) {
      for (const item of this.legendItems) {
        if (item.value !== item.lastValue) { item.value = item.lastValue; changed = true; }
      }
    } else {
      this.indicatorSeriesMap.forEach((series, key) => {
        const item = this.legendItems.find(l => l.id === key);
        if (!item) return;
        const d = params.seriesData?.get(series);
        // Hidden series or data gaps yield no point — keep the last-bar value as fallback
        const v = (d && 'value' in d && Number.isFinite(d.value)) ? this.formatLegendValue(d.value) : item.lastValue;
        if (item.value !== v) { item.value = v; changed = true; }
      });
    }
    if (changed) this.cdr.markForCheck();
  }

  public removeIndicatorFromChart(key: string): void {
    const series = this.indicatorSeriesMap.get(key);
    if (!series || !this.lwChart) return;
    try { this.lwChart.removeSeries(series); } catch {}
    this.indicatorSeriesMap.delete(key);
    this.indicatorVisibility.delete(key);
    this.legendItems = this.legendItems.filter(l => l.id !== key);
  }

  public getIndicatorKeys(): string[] {
    return Array.from(this.indicatorSeriesMap.keys());
  }

  public toggleIndicatorSeries(key: string): void {
    const series = this.indicatorSeriesMap.get(key);
    if (!series) return;
    const visible = !(this.indicatorVisibility.get(key) !== false);
    this.indicatorVisibility.set(key, visible);
    try { series.applyOptions({ visible }); } catch {}
  }

  public isIndicatorVisible(key: string): boolean {
    return this.indicatorVisibility.get(key) !== false;
  }

  // ── Drawing tools (lógica compartida estilo TradingView) ─────────────────
  public drawingCtl = new ChartDrawingController();

  tableColumnsLat: TableColmuns[] = [
    { id: 'tipo', name: 'Tipo', isShow: true, isDate: false, isNumber: false, isString: true },
    { id: 'valor1', name: 'Valor', isShow: true, isDate: false, isNumber: false, isString: true },
    { id: 'fecha', name: 'Fecha', isShow: true, isDate: true, isNumber: false, isString: false },
    { id: 'valor2', name: 'Stoploss', isShow: true, isDate: false, isNumber: false, isString: true },
    { id: 'valor4', name: 'Profit', isShow: true, isDate: false, isNumber: false, isString: true },
    { id: 'valor3', name: 'Indicador', isShow: true, isDate: false, isNumber: false, isString: true },
  ];
  displayedColumnsLat: string[] = ['fecha', 'tipo', 'valor1', 'valor2', 'valor4', 'valor3'];

  constructor(
    private _controlService: PlanificadorService,
    private _httpclient: HttpClient,
    private _route: ActivatedRoute,
    private _temaService: TemaService,
    private cdr: ChangeDetectorRef,
    private _cookieService: CookieService,
    private _sseService: SseService,
    public dialog: MatDialog,
    private companiaWebsocketService: CompaniaWebsocketService,
    private brokerTimeService: BrokerTimeService,
    private _rastreadorService: RastreadorService
  ) {}

  private formatTimeForSeries(ms: number): string | number {
    if (this._isIntraday) return Math.round(ms / 1000);
    return new Date(ms).toISOString().slice(0, 10);
  }

  // ── Drawing tools: enganche del controller compartido ─────────────────────

  private attachDrawingController(): void {
    if (!this.lwChart || !this.candleSeries) return;
    const containerEl = this.containerRef?.nativeElement ?? null;
    if (!containerEl) return;
    this.drawingCtl.attach({
      chart: this.lwChart,
      series: this.candleSeries,
      container: containerEl,
      storageKey: () =>
        this._fetchCompania ? `nectum:drawings:v1:${this._fetchCompania}:${this.activeTimeframe}` : null,
      formatTime: (ms: number) => this.formatTimeForSeries(ms) as any,
      getBarOHLC: (time: any) => this.findBarOhlc(time),
      requestText: (toolType: string) => this.openDrawingTextDialog(toolType),
      isDark$: this._temaService.valDarkTheme,
    });
    this.drawingCtl.restoreFromStorage();
  }

  private findBarOhlc(time: any): { open: number; high: number; low: number; close: number } | null {
    const cache = this._chartOhlcCache;
    if (!Array.isArray(cache) || cache.length === 0) return null;
    const target = String(time);
    const bar = cache.find((b: any) => String(b.time) === target);
    return bar ? { open: bar.open, high: bar.high, low: bar.low, close: bar.close } : null;
  }

  private openDrawingTextDialog(toolType: string): Promise<string | null> {
    const ref = this.dialog.open(DrawingTextDialogComponent, {
      data: { toolLabel: getToolLabel(toolType) },
      autoFocus: true,
    });
    return new Promise(resolve => {
      ref.afterClosed().subscribe((value: string | undefined) => resolve(value === undefined ? null : value));
    });
  }

  // ── Market countdown helpers ──────────────────────────────────────────────

  private fetchMarketSchedule(idnectum: number, metaapi: boolean): void {
    const idmercadoParam = this.idmercado > 0 ? `&idmercado=${this.idmercado}` : '';
    this._sseService.apiGetNew(`horasdemercadoporidnectum?idnectum=${idnectum}&metaapi=${metaapi}${idmercadoParam}`).subscribe({
      next: (data: any) => {
        if (Array.isArray(data) && data.length) {
          this.initializeMarketCountdown(data[0] as MarketSchedule);
        } else {
          this.marketCountdownText = '';
          this.marketSchedule = null;
          this.clearMarketCountdown();
          this.cdr.markForCheck();
        }
      },
      error: () => {
        this.marketCountdownText = '';
        this.marketSchedule = null;
        this.clearMarketCountdown();
        this.cdr.markForCheck();
      },
    });
  }

  private initializeMarketCountdown(schedule: MarketSchedule): void {
    this.marketSchedule = schedule;
    this.marketDisplayName = this._fetchMetaapi
      ? (schedule.mtServer || 'Mercado')
      : (schedule.iso || schedule.nombre || 'Mercado');
    this.marketTimezone = schedule.timezone || 'UTC';
    this.updateMarketCountdown();
    if (this.marketCountdownTimer) clearInterval(this.marketCountdownTimer);
    this.marketCountdownTimer = setInterval(() => this.updateMarketCountdown(), 1000);
  }

  private clearMarketCountdown(): void {
    if (this.marketCountdownTimer) {
      clearInterval(this.marketCountdownTimer);
      this.marketCountdownTimer = null;
    }
  }

  private updateMarketCountdown(): void {
    if (!this.marketSchedule) { this.marketCountdownText = ''; return; }
    if (this.marketSchedule.is_holiday) {
      this.marketCountdownText = 'Mercado cerrado por feriado.';
      this.isMarketOpen = false;
      this.cdr.markForCheck();
      return;
    }
    const now = new Date();
    const info = this.brokerTimeService.estadoMercado(this.marketSchedule, now);
    this.isMarketOpen = info.estado === 'ABIERTO';
    this.marketCountdownText = info.evento
      ? `${info.evento.tipo} en ${this.formatDuration(info.evento.fecha.getTime() - now.getTime())}`
      : 'Horario de mercado no disponible.';
    this.cdr.markForCheck();
  }

  private formatDuration(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts: string[] = [];
    if (hours) parts.push(`${hours}h`);
    if (minutes || hours) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return parts.join(' ');
  }

  // ── LWC data conversion helpers ───────────────────────────────────────────

  /** ms timestamp → chart time: Unix seconds for intraday, 'YYYY-MM-DD' for daily */
  private toChartTime(ms: number): any {
    return this._isIntraday ? Math.round(ms / 1000) : this.toISODate(ms);
  }

  /**
   * Normalize various `fecha` inputs into a millisecond timestamp.
   * Accepts numeric ms, numeric seconds, ISO-like strings, or date+time with '+' or space separators.
   */
  private parseFechaMs(fecha: string | number | undefined | null): number | null {
    if (fecha === null || fecha === undefined) return null;
    // If already a number
    if (typeof fecha === 'number') {
      // Heuristic: if it's in seconds (<= 1e10) convert to ms
      return fecha > 1e11 ? fecha : fecha > 1e9 ? fecha * 1000 : fecha;
    }
    let s = String(fecha).trim();
    if (!s) return null;

    // Replace leading '+' between date and time (e.g. 2022-06-08+01:15) or spaces with 'T'
    // Only replace the first separator between date and time
    s = s.replace(/^([0-9]{4}-[0-9]{2}-[0-9]{2})[ +]/, '$1T');

    // If time part has only HH:MM, append :00
    s = s.replace(/^([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2})$/, '$1:00');

    // Try parsing as ISO first
    let ms = Date.parse(s);
    if (!Number.isFinite(ms)) {
      // As a fallback, try treating as local date/time by replacing 'T' with space
      ms = Date.parse(s.replace('T', ' '));
    }
    if (!Number.isFinite(ms)) return null;
    return ms;
  }

  private sortMarkers(markers: any[]): any[] {
    return this._isIntraday
      ? markers.sort((a, b) => (a.time as number) - (b.time as number))
      : markers.sort((a, b) => String(a.time).localeCompare(String(b.time)));
  }

  /** ms timestamp → 'YYYY-MM-DD' */
  private toISODate(ms: number): string {
    return new Date(ms).toISOString().slice(0, 10);
  }

  /** Convert raw API OHLCV rows → sorted, deduped lwc CandlestickData */
  private convertToLwcOhlc(rawRows: number[][]): any[] {
    if (!Array.isArray(rawRows) || !rawRows.length) return [];
    const intraday = this._isIntraday;
    const mapped = rawRows
      .map((row: number[]) => ({
        time: intraday ? Math.round(Number(row[0]) / 1000) : this.toISODate(Number(row[0])),
        open: Number(row[1]),
        high: Number(row[2]),
        low: Number(row[3]),
        close: Number(row[4]),
      }))
      .filter(d => !isNaN(d.open) && !isNaN(d.high) && !isNaN(d.low) && !isNaN(d.close));
    if (intraday) {
      (mapped as any[]).sort((a: any, b: any) => (a.time as number) - (b.time as number));
    } else {
      (mapped as any[]).sort((a: any, b: any) => (a.time as string).localeCompare(b.time as string));
    }
    return (mapped as any[]).reduce((acc: any[], item: any) => {
      if (!acc.length || acc[acc.length - 1].time !== item.time) acc.push(item);
      return acc;
    }, []);
  }

  /** Convert raw API rows → volume HistogramData */
  private convertToLwcVolume(rawRows: number[][], upColor: string, downColor: string): any[] {
    if (!Array.isArray(rawRows) || !rawRows.length) return [];
    const mapped = rawRows.map((row: number[], idx: number) => {
      const vol = Number(row[row.length - 1]);
      const close = Number(row[4]);
      const prevClose = idx > 0 ? Number(rawRows[idx - 1][4]) : close;
      return {
        time: this.toChartTime(Number(row[0])),
        value: isFinite(vol) ? vol : 0,
        color: close >= prevClose ? upColor + '88' : downColor + '88',
      };
    });
    if (this._isIntraday) {
      mapped.sort((a, b) => (a.time as number) - (b.time as number));
    } else {
      mapped.sort((a, b) => String(a.time).localeCompare(String(b.time)));
    }
    return mapped.reduce((acc: any[], item) => {
      if (!acc.length || String(acc[acc.length - 1].time) !== String(item.time)) acc.push(item);
      return acc;
    }, []);
  }

  /** Convert an indicator's [{unixFecha, valor}] array → lwc LineData */
  private convertToLwcLine(points: any[]): any[] {
    return points
      .filter((p: any) => p.valor !== null && p.valor !== undefined && Number.isFinite(Number(p.valor)))
      .map((p: any) => ({ time: this.toChartTime(p.unixFecha), value: Number(p.valor) }))
      .sort((a, b) => String(a.time).localeCompare(String(b.time)))
      .reduce((acc: any[], item) => {
        if (!acc.length || acc[acc.length - 1].time !== item.time) acc.push(item);
        return acc;
      }, []);
  }

  // ── Chart color helpers ───────────────────────────────────────────────────

  private getChartColors() {
    return getChartColors(this.isDark);
  }

  // ── Lazy history merge (socket-driven, appends recent bars) ──────────────

  private queueFullHistoryMerge(): void {
    if (this.pendingHistoryTimer) {
      clearTimeout(this.pendingHistoryTimer);
      this.pendingHistoryTimer = null;
    }
    if (!this.lwChart || !this.fullPriceHistory.length) return;
    this.pendingHistoryTimer = setTimeout(() => {
      // An older-chunk fetch (Fase 4) is mutating fullPriceHistory right now — retry shortly
      // instead of racing it with a concurrent setData() call.
      if (this.historyMutationLock) { this.queueFullHistoryMerge(); return; }
      if (!this.lwChart || !this.candleSeries) return;
      this.historyMutationLock = true;
      try {
        const currentRange = this.lwChart.timeScale().getVisibleRange();
        const c = this.getChartColors();
        this.candleSeries.setData(this.convertToLwcOhlc(this.fullPriceHistory));
        if (this.volumeSeries) {
          this.volumeSeries.setData(this.convertToLwcVolume(this.fullPriceHistory, c.volUp, c.volDown));
        }
        if (currentRange) {
          try { this.lwChart.timeScale().setVisibleRange(currentRange); } catch {}
        }
      } finally {
        this.historyMutationLock = false;
        this.pendingHistoryTimer = null;
      }
    }, 250);
  }

  // ── ngOnInit ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (this.embedded) {
      // Lista del selector de activos: se carga en cuanto Models conoce el mercado (modelo por URL,
      // análisis restaurado o cambio de combo), sin esperar a que se seleccione una fila.
      this.subIdMercado = this._controlService.idMercado$.subscribe((id: number) => {
        if (id > 0) {
          this.idmercado = id;
          this.cargarListaCompanias();
        }
      });

      this.subAnalisisCompania = combineLatest([
        this._rastreadorService.valCompaniaSelec$,
        this._rastreadorService.valformdata$,
      ]).subscribe(([compania, formdata]: [any, any]) => {
        if (compania?.idNectum > 0) {
          // formdata.idMercado no se actualiza al cambiar el combo de Models; el subject sí (ver ModelsComponent.sincronizarIdMercado).
          this.idmercado = this._rastreadorService.idMercado.value || formdata?.idMercado || this.idmercado;
          this.condicion = formdata?.condicionStr ?? this.condicion;
          this.cargarListaCompanias();
          this.cargarActivo(compania.nombre, this._controlService.idModeloActivo, compania.idNectum, this._controlService.idModeloActivo > 0, null);
        }
      });

      // Marca en el chart los períodos de "Análisis Periodos" que corresponden a la compañía
      // activa (independiente de cargarActivo: no debe forzar una recarga de velas, solo
      // refrescar los marcadores sobre el chart ya cargado).
      this.subPeriodosCompania = combineLatest([
        this._rastreadorService.valCompaniaSelec$,
        this._rastreadorService.vallistaResultados$,
      ]).subscribe(([compania, listaResultados]: [any, any]) => {
        this.actualizarPeriodosActivos(compania?.idNectum > 0 ? compania.idNectum : null, listaResultados);
        this.rebuildEntradaSalidaMarkers();
        this.rebuildPeriodoLineSeries();
      });

      // Fuente de datos para la leyenda de período por hover: filas ya enriquecidas con
      // volatilidad (AnalisisPeriodosComponent las publica en cuanto las calcula), filtradas
      // por la compañía activa y con fechaInicio/fechaFin convertidas a ms para hit-testing.
      this.subPeriodosEnriquecidos = combineLatest([
        this._rastreadorService.valCompaniaSelec$,
        this._rastreadorService.valPeriodosEnriquecidos$,
      ]).subscribe(([compania, periodos]: [any, any]) => {
        const idNectum = compania?.idNectum > 0 ? compania.idNectum : null;
        this.periodosLegendSource = !idNectum ? [] : (periodos ?? [])
          .filter((p: any) => p.idNectum === idNectum)
          .map((p: any) => ({ ...p, inicioMs: this.aFechaMsDDMMYYYY(p.fechaInicio), finMs: this.aFechaMsDDMMYYYY(p.fechaFin) }))
          .filter((p: any) => p.inicioMs !== null && p.finMs !== null);
      });

      // Petición desde "Análisis Periodos" (icono de fila) para hacer zoom a un período.
      this.subPeriodoEnfocar = this._rastreadorService.valPeriodoEnfocar$.subscribe((p: any) => {
        if (p && Number.isFinite(p.inicioMs) && Number.isFinite(p.finMs)) {
          this.focusPeriodo(p.inicioMs, p.finMs);
        }
      });

      // Modelo nuevo generado en Models (o cambio a modo análisis): lo que hay en la gráfica es de
      // otro modelo (velas, E/S, indicadores) — se limpia hasta que se elija activo u operación.
      this.subIdModeloActivo = this._controlService.idModeloActivo$.subscribe((id: number) => {
        if (Number(this.idmodelo) > 0 && Number(this.idmodelo) !== Number(id)) {
          this.limpiarGrafica();
        }
      });

      this.subModeloSeleccionadoGrafica = this._controlService.modeloSeleccionadoGrafica$.subscribe((data: any) => {
        if (data?.idModelo > 0) {
          // El payload de Gestión/Contabilidad (ver GestionComponent.selectRow) no trae idmercado.
          // Usamos provisionalmente el mercado activo de la página (ModelsComponent.sincronizarIdMercado);
          // cargarActivo lo corrige con condicionln.idmercado de parametrosmodelo (clave en Portfolio
          // Labs, donde nadie fija ControlService.idMercado).
          this.idmercado = this._controlService.idMercado || this.idmercado;
          this.cargarListaCompanias();
          this.cargarActivo(data.nombreComp, data.idModelo, data.idNectum, true, data.fecha);
        }
      });
    } else {
      this.getNombreComp();
    }

    this.subDarktema = this._temaService.valDarkTheme.subscribe({
      next: (data: any) => {
        this.isDark = data;
        this.theme = data ? 'dark-theme' : 'light-theme';
        this.backgroundColor = data ? '#1e222d' : '#ffffff';
        this.color = data ? '#d1d4dc' : '#000000';
        if (this.lwChart) this.updateChartTheme();
      },
    });
  }

  // ── Row selection → navigate chart ───────────────────────────────────────

  /** Hay hora relevante (no 00:00) para mostrar junto a la fecha en la tabla. */
  public hasTimeOfDay(ms: number): boolean {
    const d = new Date(ms);
    return d.getHours() !== 0 || d.getMinutes() !== 0;
  }

  public tipoLabel(row: DescripcionRow): string {
    if (row.isEntrada) return 'Entrada';
    if (row.isSalida) return row.tiposalida ? `Salida · ${row.tiposalida}` : 'Salida';
    const t = row.tipo.toLowerCase();
    if (t.includes('stoploss-profit')) return 'Stop/Profit';
    if (t.includes('stoploss')) return 'Stop Loss';
    if (t.includes('profit')) return 'Profit';
    return row.tipo;
  }

  public tipoClass(row: DescripcionRow): string {
    if (row.isEntrada) return 'badge-entrada';
    if (row.isSalida) return 'badge-salida';
    const t = row.tipo.toLowerCase();
    if (t.includes('stoploss-profit')) return 'badge-mixto';
    if (t.includes('stoploss')) return 'badge-stop';
    if (t.includes('profit')) return 'badge-profit';
    return 'badge-neutro';
  }

  selectRow(row: DescripcionRow) {
    this.filaSeleccionada = row;
    const fechaMs = this.parseFechaMs(this.filaSeleccionada.fecha);
    if (fechaMs === null) return;
    if (!this.lwChart) return;
    setTimeout(() => this.encuadrarEnFecha(fechaMs), 0);
  }

  /**
   * Centra la vista en `fechaMs` mostrando INITIAL_VIEW_BARS velas (la mitad a cada lado), igual en
   * todos los timeframes. Se cuenta en velas cargadas, no en días naturales: ±N días daban ~8 barras
   * en semanal y ~42 en diario. Si a un lado hay menos velas cargadas, se ven las que haya (el
   * visibleRangeHandler pide más al acercarse al borde).
   */
  /**
   * Fecha de una operación (Gestión/Contabilidad o ?fecha= de la ruta) → ms en el marco de las velas.
   * Las velas y los marcadores E/S vienen con la hora de mercado codificada como UTC (la apertura de
   * AAPL es 09:30 "UTC"), mientras que fechaOperacion llega como instante que el navegador pinta en
   * hora local (la tabla muestra 9:31 para una entrada en la apertura). Se toma esa hora de pared
   * local (la que ve el usuario en la tabla) y se codifica como UTC; sin esto, en España la vela
   * buscada quedaba 1-2 h antes y en diario podía caer en el día anterior.
   */
  private fechaOperacionAMsVela(fecha: string | number | null | undefined): number | null {
    const ms = this.parseFechaMs(fecha);
    if (ms === null) return null;
    const d = new Date(ms);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds());
  }

  /**
   * Si hay un marcador de entrada/salida del modelo cerca de `ms` (±3 barras en intradía, ±1 día en
   * diario/semanal), devuelve su tiempo exacto: así queda centrada la E/S de la operación seleccionada
   * aunque su hora no coincida al minuto con fechaOperacion. Si no, devuelve `ms` tal cual.
   */
  private ajustarAMarcadorOperacion(ms: number | null): number | null {
    if (ms === null) return null;
    const tolerancia = this._isIntraday ? 3 * this.getBarMs() : this.DAY_MS;
    let mejor: number | null = null;
    for (const m of [...this.entradas, ...this.salidas]) {
      const t = typeof m.time === 'number' ? m.time : Date.parse(m.time);
      if (!Number.isFinite(t) || Math.abs(t - ms) > tolerancia) continue;
      if (mejor === null || Math.abs(t - ms) < Math.abs(mejor - ms)) mejor = t;
    }
    return mejor ?? ms;
  }

  /** Índice en _chartOhlcCache (ordenado por tiempo) de la vela más cercana a `ms`; -1 si no hay velas. */
  private indiceVelaMasCercana(ms: number): number {
    const velas = this._chartOhlcCache;
    if (!velas.length) return -1;
    let lo = 0, hi = velas.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((this.timeToMs(velas[mid].time) ?? 0) < ms) lo = mid + 1; else hi = mid;
    }
    if (lo > 0 && Math.abs((this.timeToMs(velas[lo - 1].time) ?? 0) - ms) < Math.abs((this.timeToMs(velas[lo].time) ?? 0) - ms)) lo--;
    return lo;
  }

  private encuadrarEnFecha(fechaMs: number): void {
    const velas = this._chartOhlcCache;
    if (!this.lwChart || !velas.length) return;
    const lo = this.indiceVelaMasCercana(fechaMs);
    const mitad = Math.floor(this.INITIAL_VIEW_BARS / 2);
    try {
      this.lwChart.timeScale().setVisibleRange({
        from: velas[Math.max(0, lo - mitad)].time,
        to: velas[Math.min(velas.length - 1, lo + mitad)].time,
      });
    } catch {}
  }

  /**
   * Lleva el chart con zoom a un período de "Análisis Periodos" ([inicio, fin] en ms, con un
   * margen a los lados). Si el período es más antiguo que la ventana de velas cargada, va
   * pidiendo chunks hacia atrás (fetchOlderChunk) hasta cubrirlo. Si el chart todavía no está
   * listo, guarda la petición y la reintenta (ver también createCharts).
   */
  public focusPeriodo(inicioMs: number, finMs: number): void {
    if (!Number.isFinite(inicioMs) || !Number.isFinite(finMs)) return;
    this._pendingFocusPeriodo = {
      inicioMs: Math.min(inicioMs, finMs),
      finMs: Math.max(inicioMs, finMs),
    };
    this.scheduleEnsureCorrectSize();
    this.applyPendingFocusPeriodo();
  }

  private applyPendingFocusPeriodo(intentos: number = 24): void {
    if (this._focusPeriodoTimer) { clearTimeout(this._focusPeriodoTimer); this._focusPeriodoTimer = null; }
    const target = this._pendingFocusPeriodo;
    if (!target) return;

    if (!this.lwChart || !this.candleSeries || !this.fullPriceHistory.length) {
      if (intentos > 0) this._focusPeriodoTimer = setTimeout(() => this.applyPendingFocusPeriodo(intentos - 1), 250);
      return;
    }

    const margenMs = Math.max((target.finMs - target.inicioMs) * 0.15, 3 * this.DAY_MS);

    // ¿Falta historia hacia atrás para cubrir el inicio del período? Cargar chunks y reintentar.
    if (this.loadedFrom != null && (target.inicioMs - margenMs) < this.loadedFrom &&
        !this.noMoreOlderHistory && intentos > 0) {
      if (!this.olderChunkInFlight) { try { this.fetchOlderChunk(); } catch {} }
      this._focusPeriodoTimer = setTimeout(() => this.applyPendingFocusPeriodo(intentos - 1), 300);
      return;
    }

    this._pendingFocusPeriodo = null;
    setTimeout(() => {
      if (!this.lwChart) return;
      try {
        if (this._isIntraday) {
          this.lwChart.timeScale().setVisibleRange({
            from: Math.round((target.inicioMs - margenMs) / 1000) as any,
            to: Math.round((target.finMs + margenMs) / 1000) as any,
          });
        } else {
          this.lwChart.timeScale().setVisibleRange({
            from: this.toISODate(target.inicioMs - margenMs) as any,
            to: this.toISODate(target.finMs + margenMs) as any,
          });
        }
      } catch {}
    }, 0);
  }

  // ── Route params & company data ───────────────────────────────────────────

  registrarCompania(input: string, event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const params = new URLSearchParams({
      idNectum: '0' + this.idActivo,
      nombreComp: selectElement.options[selectElement.selectedIndex].text,
      idModelo: '0',
      isModelo: 'modelo',
      condicion: encodeURIComponent(JSON.stringify(this.condicion)),
      idmercado: String(this.idmercado),
    });
    window.open('/grafica-modelo/' + params.toString(), '_blank');
  }

  /**
   * Segmento `:data` tal como está en la barra de direcciones. Angular ya decodifica '%2B' → '+'
   * en params.get('data') y URLSearchParams convierte luego ese '+' en espacio ('AAPL+' llegaba
   * como 'AAPL '). Solo para el formato de URLSearchParams (`clave=valor&...`): el antiguo
   * `idNectum%3D...` sigue leyéndose del parámetro ya decodificado.
   */
  private datosRutaSinDecodificar(): string | null {
    const prefijo = '/grafica-modelo/';
    const path = window.location.pathname;
    if (!path.startsWith(prefijo)) return null;
    const raw = path.substring(prefijo.length);
    return /^[^%]*=/.test(raw) ? raw : null;
  }

  getNombreComp() {
    this._route.paramMap.subscribe((params) => {
      const datos = params.get('data');
      let fechaParam: string | null = null;

      if (datos) {
        const searchParams = new URLSearchParams(this.datosRutaSinDecodificar() ?? datos);
        const dataObj: DataObject = {};
        searchParams.forEach((value, key) => { dataObj[key] = value; });

        if (dataObj['fecha']) fechaParam = dataObj['fecha'];
        if (dataObj['idmercado']) this.idmercado = Number(dataObj['idmercado']);

        this.cargarListaCompanias();

        this.condicion = decodeURIComponent(dataObj['condicion']);
        this.idnectum = Number(dataObj['idNectum']);

        if (dataObj['idNectum'] !== '0') {
          this.cargarActivo(dataObj['nombreComp'], dataObj['idModelo'], dataObj['idNectum'], dataObj['isModelo'] === 'modelo', fechaParam);
        }
      }
    });
  }

  /** dd/MM/yyyy -> ms (formato en el que llegan fechaInicio/fechaFin de "Análisis Periodos"). */
  private aFechaMsDDMMYYYY(fecha: string): number | null {
    if (!fecha) return null;
    const partes = fecha.split('/');
    if (partes.length !== 3) return null;
    return this.parseFechaMs(`${partes[2]}-${partes[1]}-${partes[0]}`);
  }

  /** Recalcula this.periodosActivos (períodos de "Análisis Periodos" de la compañía dada) a partir de la lista completa de resultados de RastreadorService. */
  private actualizarPeriodosActivos(idNectum: number | null, listaResultados: any): void {
    if (!idNectum) { this.periodosActivos = []; return; }
    const parsed = typeof listaResultados === 'string' ? JSON.parse(listaResultados || '[]') : listaResultados;
    this.periodosActivos = (Array.isArray(parsed) ? parsed : [])
      .filter((item: any) => item.idNectum === idNectum)
      .map((item: any) => ({
        inicioMs: this.aFechaMsDDMMYYYY(item.fechaInicio),
        finMs: this.aFechaMsDDMMYYYY(item.fechaFin),
        valorInicio: Number(item.valorFinFI),
        valorFin: Number(item.valorFinFF),
      }))
      .filter((p: { inicioMs: number | null; finMs: number | null; valorInicio: number; valorFin: number }): p is { inicioMs: number; finMs: number; valorInicio: number; valorFin: number } =>
        p.inicioMs !== null && p.finMs !== null && Number.isFinite(p.valorInicio) && Number.isFinite(p.valorFin));
  }

  /** Carga this.companias (lista del mercado activo) para el selector de activos embebido. No repite la llamada si idmercado no cambió desde la última carga. */
  private cargarListaCompanias(): void {
    if (!(this.idmercado > 0) || this.idmercado === this._idmercadoCompaniasCargado) return;
    const idmercado = this.idmercado;
    this._idmercadoCompaniasCargado = idmercado;
    this._sseService.apiPostNewSinToken('listadecompaniaspormercadossintoken', { idmercado }).subscribe({
      next: (data: any) => {
        // idMercado$ arranca en 8 y luego emite el mercado real: descartar respuestas de un mercado anterior.
        if (idmercado !== this._idmercadoCompaniasCargado) return;
        this.companias = data;
        if (this.selectedCompanyId != null && !(data ?? []).some((c: any) => c.idNectum === this.selectedCompanyId)) {
          this.selectedCompanyId = null;
        }
      },
    });
  }

  /** Selector de activos del modo embebido: cambia manualmente qué compañía se muestra, conservando la condición del análisis activo (this.condicion, evaluada en fetchData/rama sin modelo). */
  public onAssetSelectorChange(): void {
    const compania = (this.companias ?? []).find((c: any) => c.idNectum === this.selectedCompanyId);
    if (compania) {
      // Mantiene el modelo que ya está en la gráfica (p. ej. el de la fila de Gestión/Contabilidad);
      // idModeloActivo solo existe cuando Models se abrió con ?id= y es 0 en el resto de casos;
      // en Portfolio Labs (idModeloActivo = 0) se usa el modelo del combo "Modelo".
      const idModelo = Number(this.idmodelo) > 0
        ? Number(this.idmodelo)
        : (this.modelosLista.length && this.selectedModeloId ? this.selectedModeloId : this._controlService.idModeloActivo);
      // console.log('[DEBUG] onAssetSelectorChange', { iso: compania.iso, idNectum: this.selectedCompanyId, idModelo, thisIdmodelo: this.idmodelo, idModeloActivo: this._controlService.idModeloActivo });
      this.cargarActivo(compania.iso, idModelo, this.selectedCompanyId, idModelo > 0, null);
    }
  }

  /** Fija el modelo del combo y el mercado de su lista de activos, sin cargar nada en la gráfica. */
  private async seleccionarModelo(m: ModeloGraficaOpcion): Promise<void> {
    this.selectedModeloId = m.idmodelo;
    const idmercado = await this.resolverMercadoModelo(m);
    // El usuario pudo cambiar de modelo mientras se resolvía el mercado.
    if (this.selectedModeloId !== m.idmodelo) return;
    if (idmercado > 0) this.idmercado = idmercado;
    this.cargarListaCompanias();
  }

  /**
   * Mercado del modelo = condicionln.idmercado de parametrosmodelo (mismo criterio que fetchData).
   * tbl_cartera.idmercado puede venir a 0, así que solo se usa como fallback. Cacheado por modelo.
   */
  private async resolverMercadoModelo(m: ModeloGraficaOpcion): Promise<number> {
    const cache = this._mercadoPorModelo.get(m.idmodelo);
    if (cache) return cache;
    let idmercado = 0;
    try {
      const params: any = await this._sseService.apiGetNew(`parametrosmodelo?idmodelo=${m.idmodelo}`).toPromise();
      idmercado = Number(JSON.parse(params?.condicionln ?? '{}')?.idmercado) || 0;
    } catch (e) { console.warn('parametrosmodelo (mercado del modelo) falló', e); }
    if (!(idmercado > 0)) idmercado = Number(m.idmercado) || 0;
    if (idmercado > 0) this._mercadoPorModelo.set(m.idmodelo, idmercado);
    return idmercado;
  }
  private _mercadoPorModelo = new Map<number, number>();

  /**
   * Combo "Modelo" (Portfolio Labs): si el activo mostrado es del mercado del nuevo modelo, se recarga
   * con él; si no, se limpia la gráfica y se carga la lista de activos del nuevo mercado.
   */
  public async onModeloSelectorChange(): Promise<void> {
    const m = this.modelosLista.find(x => x.idmodelo === this.selectedModeloId);
    if (!m) return;
    const idmercado = await this.resolverMercadoModelo(m);
    if (this.selectedModeloId !== m.idmodelo) return;
    const mismoMercado = !(idmercado > 0) || idmercado === this.idmercado;
    if (mismoMercado && this.nombreCompania && this.idnectum > 0) {
      this.cargarActivo(this.nombreCompania, m.idmodelo, this.idnectum, true, null);
      return;
    }
    this.limpiarGrafica();
    this.seleccionarModelo(m);
  }

  /** Punto único de carga de un activo (compañía + opcionalmente modelo) en el gráfico, usado tanto por el modo ruta (getNombreComp) como por el modo embebido dentro de Models. Devuelve la promesa de fetchData por si el llamador necesita encadenar algo una vez el chart esté listo. */
  private cargarActivo(nombreComp: string, idModelo: any, idNectum: any, isModeloFlag: boolean, fecha?: string | null): Promise<void> {
    // fetchData() (rama con modelo) arma idnectums desde this.idnectum, no desde el parámetro
    // idNectum — hay que mantenerlo sincronizado acá, único punto de entrada para cargar un activo.
    this.idnectum = Number(idNectum);
    const fetchPromise = this.fetchData(nombreComp, idModelo, idNectum, fecha ?? null);
    // Si esta carga fue disparada mientras la pestaña "Gráfica Análisis" no era la activa,
    // el contenedor está "detached" del DOM (ver @ViewChild) y el chart se crea con un tamaño
    // de fallback (window.innerWidth) que no coincide con el tamaño real de la pestaña. En vez
    // de un solo intento a ciegas, reintentar hasta que el contenedor realmente tenga tamaño.
    // También aplica en la ruta /grafica-modelo: la altura del header no es fija.
    this.scheduleEnsureCorrectSize();
    this.nombreCompania = nombreComp;
    this.idmodelo = idModelo;
    if (Number(idModelo) > 0) this.selectedModeloId = Number(idModelo);
    this.isModelo = isModeloFlag;
    this.selectedCompanyId = Number(idNectum) || null;

    this.subscription?.unsubscribe();
    this.subscription = this.companiaWebsocketService
      .getCompaniaUpdates()
      .subscribe(
        (data) => { this.companiaData = data; this.onSocketUpdate(data); },
        (error) => console.error('WS Error:', error)
      );
    return fetchPromise;
  }

  // ── fetchData ─────────────────────────────────────────────────────────────

  private resetDescripcionState(): void {
    this.dataAdicional.data = [];
    this.descripcionRowsCount = 0;
    this.filaSeleccionada = null;
    this.entradas = [];
    this.salidas = [];
    this.operaciones = [];
    this.openTradeLevels = null;
  }

  /**
   * SL/TP vigentes de la última ENTRADA sin SALIDA posterior (null si no hay operación abierta).
   * Las filas `stoploss`/`profit` que siguen a la entrada traen el nivel actualizado (stop dinámico),
   * así que gana la última. Solo se devuelven los niveles que el modelo tiene activos.
   */
  private computeOpenTradeLevels(rows: DescripcionRow[], condicionModelo: any): OpenTradeLevels | null {
    let idxEntrada = -1;
    for (let i = rows.length - 1; i >= 0; i--) {
      if (rows[i].isEntrada) { idxEntrada = i; break; }
    }
    if (idxEntrada < 0 || rows.slice(idxEntrada + 1).some(r => r.isSalida)) return null;
    let sl = NaN, tp = NaN;
    for (const row of rows.slice(idxEntrada)) {
      const t = row.tipo.toLowerCase();
      if (!row.isEntrada && !t.includes('stoploss') && !t.includes('profit')) continue;
      sl = Number(row.valor2);
      tp = Number(row.valor4);
    }
    return {
      stoploss: condicionModelo?.stopLossActivo !== false ? sl : null,
      profit: condicionModelo?.proFitActivo !== false ? tp : null,
    };
  }

  private applyOpenTradeLines(): void {
    this.openTradePriceLines = applyOpenTradeLines(this.candleSeries, this.openTradePriceLines, this.openTradeLevels);
  }

  private buildDescripcionRows(descripciones: any[]): DescripcionRow[] {
    if (!Array.isArray(descripciones)) return [];

    return descripciones.map((obj: any, index: number) => {
      const valorParts = String(obj?.valor ?? '').split('_');
      const valor1 = valorParts[0] ?? '';
      const valor2 = valorParts[1] ?? '';
      const valor4 = valorParts[2] ?? '';
      const tipo = String(obj?.tipo ?? '');
      const fecha = Number(obj?.fecha ?? 0);

      return {
        ...obj,
        tipo,
        valor: String(obj?.valor ?? ''),
        fecha,
        valor1,
        valor2,
        valor3: obj?.ind ? valor1 : 0,
        valor4,
        isEntrada: tipo.includes('ENTRADA'),
        isSalida: tipo.includes('SALIDA'),
        rowKey: `${fecha}-${tipo}-${index}`,
      };
    });
  }

  trackByDescripcionRow = (_index: number, row: DescripcionRow): string => row.rowKey;

  /**
   * Ventana inicial de velas [desde, hasta] para el fetch de `historicos-compania`.
   * Si se pasó `fecha` (navegar a una operación puntual), la ventana se centra en esa
   * fecha en vez de en "ahora", para que las velas alrededor del punto a resaltar ya
   * estén cargadas (si no, `setVisibleRange` en `createCharts` apuntaría a un rango sin
   * datos). `hasta` no puede superar `nowMs` (no hay datos futuros).
   */
  private computeInitialWindow(nowMs: number, fechaMs: number | null, windowDaysOverride?: number): { desde: number; hasta: number } {
    const windowMs = (windowDaysOverride ?? this.getInitialWindowDays()) * this.DAY_MS;
    let result: { desde: number; hasta: number };
    if (fechaMs !== null) {
      const hasta = Math.min(nowMs, fechaMs + windowMs / 2);
      result = { desde: hasta - windowMs, hasta };
    } else {
      result = { desde: nowMs - windowMs, hasta: nowMs };
    }
    console.log('[DEBUG] computeInitialWindow', {
      nowMs, nowIso: new Date(nowMs).toISOString(),
      fechaMs, fechaIso: fechaMs ? new Date(fechaMs).toISOString() : null,
      windowDays: this.getInitialWindowDays(), activeTimeframe: this.activeTimeframe,
      desdeIso: new Date(result.desde).toISOString(), hastaIso: new Date(result.hasta).toISOString(),
    });
    return result;
  }

  /**
   * Pide `historicos-compania` con la ventana inicial de `computeInitialWindow`; si se pasó
   * `fecha` (navegar a una operación/fila puntual) y la ventana centrada en esa fecha vuelve
   * vacía (feriado, gap, símbolo sin cotización justo ese día), la ensancha progresivamente
   * y reintenta antes de rendirse, en vez de dejar el gráfico en blanco por un único intento
   * centrado exactamente en un punto sin datos. Sin `fecha` no hay nada que ensanchar (la
   * ventana ya es "los últimos N días hasta ahora", que solo puede volver vacía si el símbolo
   * no tiene ningún historial).
   */
  private async fetchInitialWindowWithFallback(
    compania: string, metaapi: boolean, usePeriodo: number, nowMs: number, fechaMs: number | null,
  ): Promise<any[]> {
    const MAX_WINDOW_DAYS = 730; // tope de ensanche: ±2 años alrededor de la fecha pedida
    let windowDays = this.getInitialWindowDays();
    let data: any[] = [];
    for (;;) {
      const { desde, hasta } = this.computeInitialWindow(nowMs, fechaMs, windowDays);
      this.loadedFrom = desde;
      this.loadedTo = hasta;
      const url = `historicos-compania?iso=${encodeURIComponent(compania)}&metaapi=${metaapi}&periodo=${usePeriodo}&desde=${desde}&hasta=${hasta}`;
      data = (await this._sseService.apiGetNew(url).toPromise()) ?? [];
      if (data.length || fechaMs === null || windowDays >= MAX_WINDOW_DAYS) return data;
      windowDays = Math.min(windowDays * 4, MAX_WINDOW_DAYS);
    }
  }

  private showNoDataForFechaWarning(fecha: string | null | undefined): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '380px', height: '220px', panelClass: this.theme,
      data: { msj: `No se encontraron velas disponibles cerca de la fecha ${fecha ?? ''} para este símbolo.` },
    });
  }

  async fetchData(compania: any, idModelo: any, idNectum: any, fecha?: string | null, periodo?: number) {
    this._fetchCompania = compania;
    this._fetchIdModelo = idModelo;
    this._fetchIdNectum = idNectum;
    this._fetchFecha = fecha ?? null;
    this.resetDescripcionState();

    // Bounded initial window for candles: avoid downloading the entire history up front
    // (Fase 3). Sized per timeframe (más chica para minutos) y centrada en `fecha` (si se
    // pasó) más abajo, una vez resuelto el periodo real del modelo. Older bars are fetched
    // on demand as the user scrolls back (Fase 4). `obtenerdescripcion` (entradas/salidas)
    // se pide siempre completo.
    const nowMs = Date.now();
    const fechaMs = fecha ? this.fechaOperacionAMsVela(fecha) : null;
    this.loadedFrom = null;
    this.loadedTo = null;
    this.noMoreOlderHistory = false;
    this.noMoreNewerHistory = false;
    this.olderEmptyStreak = 0;
    this.newerEmptyStreak = 0;
    this.olderForceChunk = false;
    this.newerForceChunk = false;
    // Invalida cualquier loop de setZoomRange('All'/preset) que siga corriendo de una carga anterior.
    this._rangeRequestSeq++;
    this.rangeLoading = false;
    this.olderChunkInFlight = false;
    this.newerChunkInFlight = false;
    this.historyMutationLock = false;

    const urldescripcion = `obtenerdescripcion?idmodelo=${idModelo}&idnectum=${idNectum}`;
    this.beginCargaVelas();
    try {
      const DatosDescripcion = await this._sseService.apiGetNew(urldescripcion).toPromise();
      const newArray = this.buildDescripcionRows(DatosDescripcion ?? []);
      // console.log('[DEBUG] obtenerdescripcion', { url: urldescripcion, compania, filas: newArray.length,
      //   entradas: newArray.filter(r => r.isEntrada).length, salidas: newArray.filter(r => r.isSalida).length,
      //   primera: newArray[0] ? new Date(newArray[0].fecha).toISOString() : null });

      newArray.forEach((elemento: DescripcionRow) => {
        if (elemento.tipo.includes('ENTRADA')) {
          this.entradas.push({ time: elemento.fecha, position: 'belowBar', color: '#26a69a', shape: 'arrowUp', text: 'E' });
          this.operaciones.push([elemento.fecha, Number(elemento.valor)]);
        } else if (elemento.tipo.includes('SALIDA')) {
          this.salidas.push({ time: elemento.fecha, position: 'aboveBar', color: '#ef5350', shape: 'arrowDown', text: elemento.tiposalida || 'S' });
          this.operaciones.push([elemento.fecha, Number(elemento.valor)]);
        }
      });

      let list_conditions_entry = 0;
      let list_conditions_exit = 0;
      let indicadorsIa: any = {};

      try {
        if (this.idmodelo > 0) {
          const urlParametrosModelo = `parametrosmodelo?idmodelo=${this.idmodelo}`;
          const APIparams = await this._sseService.apiGetNew(urlParametrosModelo).toPromise();
          // Parseo local: this.jsoncondicon se asigna más abajo, tras la descarga de velas, porque
          // triggerCastConditionsFromSocket lo lee y no debe ver la condición sin procesar.
          const condicionModelo = JSON.parse(APIparams.condicionln);
          this.openTradeLevels = this.computeOpenTradeLevels(newArray, condicionModelo);
          // Mercado por defecto = el del modelo (condicionln.idmercado). En Portfolio Labs nadie fija
          // ControlService.idMercado; en Models coincide con el combo. Antes de fetchMarketSchedule,
          // que lo usa como parámetro.
          const idmercadoModelo = Number(condicionModelo?.idmercado);
          if (idmercadoModelo > 0 && idmercadoModelo !== this.idmercado) {
            this.idmercado = idmercadoModelo;
            if (this.embedded) this.cargarListaCompanias();
          }
          const metaapi: boolean = APIparams.metaapi === true;
          this._fetchMetaapi = metaapi;
          this.fetchMarketSchedule(this.idnectum, metaapi);
          const usePeriodo = periodo ?? APIparams.periodo ?? 6;
          if (!periodo) {
            this.modeloPeriodo = usePeriodo;
            this.activeTimeframe = PERIODO_TO_TIMEFRAME[usePeriodo] ?? 'D';
          }
          this._isIntraday = isPeriodoIntraday(usePeriodo);
          const API = await this.fetchInitialWindowWithFallback(compania, metaapi, usePeriodo, nowMs, fechaMs);

          this.strcondicion = APIparams.condicionln;
          this.condicion = APIparams.condicion;
          this.jsoncondicon = condicionModelo;
          this.setCondicionLegible(this.jsoncondicon.ln);
          list_conditions_entry = this.jsoncondicon.list_conditions_entry.length;
          list_conditions_exit = this.jsoncondicon.list_conditions_exit.length;
          this.jsoncondicon.list_conditions_entry = castEntryConditions(this.condicion, this.jsoncondicon.list_conditions_exit);
          this.jsoncondicon.list_conditions_exit = [];
          delete this.jsoncondicon.ln;
          this.jsoncondicon.idnectums = [this.idnectum];
          this.jsoncondicon.includePatterns = this.extractPatternsFromConditions(
            this.jsoncondicon.list_conditions_entry ?? [],
            this.jsoncondicon.list_conditions_exit ?? []
          );
          const { start: startDateStr2, end: endDateStr2 } = this.computeStartEnd(this.jsoncondicon.start);
          this.jsoncondicon.start = startDateStr2;
          this.jsoncondicon.end = endDateStr2;
          
          const response = await fetch(global.url + 'calculo/cast_conditions', {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(this.jsoncondicon),
          });
          if (response.ok) {
            indicadorsIa = await response.json();
            if (indicadorsIa.result === 'error') {
              this.showCondicionError();
            }
          }
          this.dataGra = API;
        } else {
          const usePeriodo = periodo ?? this.modeloPeriodo;
          this._fetchMetaapi = false;
          this.fetchMarketSchedule(this.idnectum, false);
          this.activeTimeframe = PERIODO_TO_TIMEFRAME[usePeriodo] ?? 'D';
          this._isIntraday = isPeriodoIntraday(usePeriodo);
          this.dataGra = await this.fetchInitialWindowWithFallback(compania, false, usePeriodo, nowMs, fechaMs);

          // Sin modelo guardado: si hay una condición activa (seguimiento del análisis en vivo,
          // selector de activos embebido, o período), evaluarla contra el activo mostrado.
          // Fuente principal: PlanificadorService.interpretacionDataListEntry/Exit — el mismo
          // shape estructurado que ModelsComponent.funcionaEstadisticas() ya manda al backend
          // para la corrida en vivo (RastreadorService.formdata.condicionStr NO se puebla en ese
          // flujo, solo en la restauración por ?modo=analisis, de ahí el fallback a this.condicion).
          const listEntry = this._controlService.interpretacionDataListEntry?.length
            ? this._controlService.interpretacionDataListEntry
            : (this.condicion && this.condicion !== 'undefined' ? [{ dsl: this.condicion }] : null);

          if (listEntry) {
            const listExit = this._controlService.interpretacionDataListEntry?.length
              ? (this._controlService.interpretacionDataListExit ?? [])
              : [];
            if (this._controlService.interpretacionDataListEntry?.length) {
              this.setCondicionLegible(this._controlService.condiciones);
            } else {
              this.condicionEntradaLista = [this.condicion];
              this.condicionSalidaLista = [];
            }
            try {
              // Mismo cálculo de start/end que usa la rama con modelo (computeStartEnd), tomando
              // como base el fechaInicio del modelo cargado en /models si hay uno (ver
              // PlanificadorService.idModeloActivo/formdata) — sin esto el backend no tiene rango
              // de fechas válido y cast_conditions no devuelve indicadores.
              const { start, end } = this.computeStartEnd(this._controlService.formdata?.value?.fechaInicio);
              const includePatterns = this.extractPatternsFromConditions(listEntry, listExit);
              const response = await fetch(global.url + 'calculo/cast_conditions', {
                method: 'POST',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  list_conditions_entry: listEntry,
                  list_conditions_exit: listExit,
                  idnectums: [idNectum],
                  periodo: usePeriodo,
                  metaapi: false,
                  start,
                  end,
                  includePatterns,
                  // Modelo cargado en /models (?id=<idModelo> sin modo=analisis), 0 en modo análisis.
                  idmodelo: this._controlService.idModeloActivo,
                }),
              });
              if (response.ok) indicadorsIa = await response.json();
            } catch (e) { console.warn('cast_conditions (sin modelo) falló', e); }
          } else {
            this.condicionEntradaLista = [];
            this.condicionSalidaLista = [];
          }
        }

        this.companiaWebsocketService.connect(this.idnectum, {
          metaapi: this._fetchMetaapi,
          timeframe: this.activeTimeframe,
        });

        this.dataAdicional.data = newArray;
        this.descripcionRowsCount = newArray.length;
        if (fechaMs !== null && (!Array.isArray(this.dataGra) || this.dataGra.length === 0)) {
          this.showNoDataForFechaWarning(fecha);
        }
        await this.createCharts(this.dataGra, compania, indicadorsIa, list_conditions_entry, list_conditions_exit, fecha);
      } catch (err) {
        console.error(err);
        this.showCondicionError();
      }
    } catch (error) {
      console.error('Error al recuperar datos:', error);
    } finally {
      this.endCargaVelas();
    }
  }

  private setCondicionLegible(ln: any[] | undefined): void {
    const { entrada, salida } = splitCondicionLegible(ln);
    this.condicionEntradaLista = entrada;
    this.condicionSalidaLista = salida;
  }

  private showCondicionError() {
    let stringg = this.condicion?.replace(/\\"/g, '"') ?? '';
    stringg = stringg.replace('["idnctm"]', '[' + this.idnectum + ']');
    stringg = stringg.substring(1, stringg.length - 1);
    this.dialog.open(ConfirmDialogComponent, {
      width: '380px', height: '250px', panelClass: this.theme,
      data: { msj: 'Error en ejecutar la condicion: ' + stringg },
    });
  }

  // ── createCharts (main migration target) ─────────────────────────────────

  async createCharts(
    data: any,
    compania: any,
    indicadorsIa: any,
    list_conditions_entry: any,
    list_conditions_exit: any,
    fecha?: string | null
  ) {
    if (this.pendingHistoryTimer) { clearTimeout(this.pendingHistoryTimer); this.pendingHistoryTimer = null; }

    // Destroy existing chart if any
    this.destroyChart();

    const APIratio = await this._sseService.apiGetNew('listaratios').toPromise();
    const c = this.getChartColors();

    // ── Store loaded history window for lazy merge ────────────────────────
    this.fullPriceHistory = this.normalizeHistoryRows(data);
    this.latestKnownMs = 0;
    if (Array.isArray(indicadorsIa?.result)) {
      for (const it of indicadorsIa.result) {
        const ms = Date.parse(it?.fecha);
        if (Number.isFinite(ms) && ms > this.latestKnownMs) this.latestKnownMs = ms;
      }
    }
    this.fullVolumeHistory = this.fullPriceHistory.map((r: any[]) => [r[0], r[r.length - 1]] as [number, number | null]);
    this._chartOhlcCache = this.convertToLwcOhlc(this.fullPriceHistory);

    // `data` is already bounded to the initial window by the backend (Fase 3) —
    // older bars are fetched on demand as the user scrolls back (Fase 4).
    const ohlcData = this._chartOhlcCache;
    const volData = this.convertToLwcVolume(this.fullPriceHistory, c.volUp, c.volDown);

    // ── Create chart ─────────────────────────────────────────────────────
    const containerEl = this.containerRef?.nativeElement ?? null;
    if (!containerEl) { console.warn('Chart container #container not found'); return; }

    // Use getBoundingClientRect for post-layout accurate dimensions
    const rect = containerEl.getBoundingClientRect();
    const chartWidth  = rect.width  || window.innerWidth;
    const chartHeight = rect.height || (window.innerHeight - 100);

    this.lwChart = createChart(containerEl, {
      width: chartWidth,
      height: chartHeight,
      layout: {
        background: { type: ColorType.Solid, color: c.background },
        textColor: c.text,
        attributionLogo: false,
      },
      // Reduce top whitespace on the main (right) price scale so candles aren't pushed
      // too far down when there are overlay series or tight price ranges.
      rightPriceScale: { borderColor: c.border, scaleMargins: { top: 0.12, bottom: 0.02 } },
      grid: {
        vertLines: { color: c.grid },
        horzLines: { color: c.grid },
      },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: {
        borderColor: c.border,
        timeVisible: this._isIntraday,
        secondsVisible: false,
        minBarSpacing: this._isIntraday ? 0.05 : 0.5,
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
    });

    // Subscribe crosshair move for OHLC legend
    if (this.crosshairHandler) {
      try { this.lwChart.unsubscribeCrosshairMove(this.crosshairHandler); } catch {}
    }
    const ohlcCache = this._chartOhlcCache;
    const updateOhlc = (o: number|null, h: number|null, l: number|null, c: number|null, prevC: number|null) => {
      const change = (c !== null && prevC !== null) ? c - prevC : null;
      const changePct = (change !== null && prevC !== null && prevC !== 0) ? (change / prevC) * 100 : null;
      this.crosshairOhlc = { open: o, high: h, low: l, close: c, change, changePct };
      this.cdr.markForCheck();
    };
    this.crosshairHandler = (params: any) => {
      this.updateLegendValuesAtCrosshair(params);
      // Leyenda de período por hover (modo embebido): busca si el punto bajo el cursor cae
      // dentro del rango [inicioMs, finMs] de algún período de "Análisis Periodos".
      const hoveredMs = params.time ? this.parseFechaMs(params.time as any) : null;
      this.hoveredPeriodoLegend = (this.embedded && hoveredMs !== null)
        ? (this.periodosLegendSource.find((p: any) => hoveredMs >= p.inicioMs && hoveredMs <= p.finMs) ?? null)
        : null;
      if (!this.candleSeries) return;
      if (!params.time) {
        const last = ohlcCache[ohlcCache.length - 1];
        const prev = ohlcCache.length > 1 ? ohlcCache[ohlcCache.length - 2] : null;
        if (last) updateOhlc(last.open, last.high, last.low, last.close, prev?.close ?? last.open);
        return;
      }
      const d = params.seriesData?.get(this.candleSeries);
      if (!d) return;
      if ('open' in d) {
        const idx = ohlcCache.findIndex((bar: any) => String(bar.time) === String(params.time));
        updateOhlc(d.open, d.high, d.low, d.close, idx > 0 ? ohlcCache[idx - 1].close : d.open);
      } else if ('value' in d) {
        updateOhlc(null, null, null, d.value, null);
      }
    };
    this.lwChart.subscribeCrosshairMove(this.crosshairHandler);
    // Init with last bar
    const _last = ohlcCache[ohlcCache.length - 1];
    const _prev = ohlcCache.length > 1 ? ohlcCache[ohlcCache.length - 2] : null;
    if (_last) updateOhlc(_last.open, _last.high, _last.low, _last.close, _prev?.close ?? _last.open);

    // Load older/newer bars as the user scrolls/zooms toward either edge of loaded history (Fase 4)
    if (this.visibleRangeHandler) {
      try { this.lwChart.timeScale().unsubscribeVisibleLogicalRangeChange(this.visibleRangeHandler); } catch {}
    }
    this.visibleRangeHandler = (range: any) => {
      console.log('[DEBUG] visibleRangeHandler fired', range, 'fullPriceHistory.length=', this.fullPriceHistory.length);
      if (!range) return;
      this.maybeLoadOlderChunk(range);
      this.maybeLoadNewerChunk(range);
    };
    this.lwChart.timeScale().subscribeVisibleLogicalRangeChange(this.visibleRangeHandler);

    // ── Candlestick series ───────────────────────────────────────────────
    this.candleSeries = this.lwChart.addSeries(CandlestickSeries, {
      upColor: c.upColor,
      downColor: c.downColor,
      borderVisible: false,
      wickUpColor: c.upColor,
      wickDownColor: c.downColor,
      priceFormat: { type: 'price', precision: 5, minMove: 0.00001 },
    });
    this.candleSeries.setData(ohlcData);

    // ── Volume histogram (separate price scale, bottom 20%) ──────────────
    this.volumeSeries = this.lwChart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    this.lwChart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });
    this.volumeSeries.setData(volData);
    if (volData && volData.length) this.lastVolumeTime = volData[volData.length - 1].time;

    // ── Entry / Exit markers ─────────────────────────────────────────────
    // SL/TP antes que los marcadores: createPriceLine refresca la serie en el acto y, si los
    // marcadores ya están puestos, fija sus índices de barra antes de que los indicadores añadan
    // fechas más antiguas al eje → quedan desplazados fuera de la vista.
    this.applyOpenTradeLines();
    this.rebuildEntradaSalidaMarkers();

    // ── Trade operation lines (entry→exit pairs) — un solo primitive para todos los trades ──
    this.rebuildOperLineSeries();
    this.rebuildPeriodoLineSeries();

    // ── IA Indicators ────────────────────────────────────────────────────
    if (indicadorsIa?.result !== undefined && Array.isArray(indicadorsIa.result)) {
      const keys = Object.keys(indicadorsIa.result[0] ?? {});
      const keysToRemove = ['close', 'low', 'high', 'open', 'volume', 'fecha', 'dsl_0', 'entry_decision_final', 'exit_decision_final'];
      const ketss = keys.filter((k) => !keysToRemove.includes(k) && !k.startsWith('pattern_'));

      // Group raw indicator data
      ketss.forEach((key) => { if (!this.groupedIndicators[key]) this.groupedIndicators[key] = []; });

      indicadorsIa.result.forEach((item: any) => {
        const unixFecha = Date.parse(item.fecha);
        ketss.forEach((key) => {
          let valor = item[key];
          if (typeof valor === 'string' && valor.toUpperCase() === 'NAN') valor = null;
          else if (typeof valor === 'number' && !Number.isFinite(valor)) valor = null;
          this.groupedIndicators[key].push({ unixFecha, valor });
        });
      });

      // ── Pass 1: determine oscillator status without self-reference ──────────
      // Build a Set of prefixes whose keys are oscillators (for cross-key lookup)
      const oscillatorPrefixSet = new Set<string>();
      for (const key of ketss) {
        const condicionbuscar = key.replaceAll('#', '').split(':')[1];
        if (!condicionbuscar) continue;
        let isOsc = this.oscillators.some((s) => condicionbuscar.toLowerCase().includes(s.toLowerCase()));
        if (key.toLowerCase().includes('sma:_')) isOsc = false;
        if (Array.isArray(APIratio) && APIratio.some((r: string) => r.toLowerCase() === key)) isOsc = true;
        if (isOsc) oscillatorPrefixSet.add(key.split(':')[0]);
      }

      // ── Pass 2: build arrayAxis using the completed oscillatorPrefixSet ─────
      const arrayAxis: Array<{ nombre: string; isOscillator: boolean }> = ketss.map((key) => {
        const condicionbuscar = key.replaceAll('#', '').split(':')[1];
        let isOscillator = false;
        if (condicionbuscar) {
          isOscillator = this.oscillators.some((s) => condicionbuscar.toLowerCase().includes(s.toLowerCase()));
          if (key.toLowerCase().includes('sma:_')) isOscillator = false;
          // Also mark as oscillator if the key's prefix is already known as oscillator
          if (!isOscillator) {
            const prefix = key.split(':')[0];
            if (oscillatorPrefixSet.has(prefix)) isOscillator = true;
          }
        }
        if (Array.isArray(APIratio) && APIratio.some((r: string) => r.toLowerCase() === key)) isOscillator = true;
        return { nombre: key.split(':')[0], isOscillator };
      });

      // Build indicator payloads for progressive loading
      const indicatorPayloads: Array<{
        id: string; name: string; isOscillator: boolean;
        isDecision: boolean; data: any[];
      }> = [];
      let contadorentry = 0;

      for (let i = 0; i < ketss.length; i++) {
        const key = ketss[i];
        const isDecision = key.includes('entry_decision');
        const isOscillator = arrayAxis[i]?.isOscillator ?? false;

        // Build lwc line data
        const raw = this.groupedIndicators[key] as any[];
        const lineData = this.convertToLwcLine(raw);

        let nombre = key;
        if (key.includes('entry')) {
          contadorentry++;
          if (contadorentry > list_conditions_entry) nombre = nombre.replace('entry', 'exit');
        }

        const idactivorr = nombre.split('_')[0];
        let displayName = nombre;
        if (idactivorr !== 'dsl') {
          // async name resolution — push with placeholder, update later
          const jsonData = { idNectum: Number(idactivorr) };
          this._sseService.apiPostNewSinToken('ia/obtenerNombreActivo', jsonData).subscribe({
            next: (data: any) => {
              const existing = this.indicatorSeriesMap.get(nombre);
              if (existing) {
                try { existing.applyOptions({ title: nombre.replace(idactivorr, data.nombre) }); } catch {}
              }
            },
          });
        } else {
          displayName = nombre.replace('0', this.nombreCompania);
        }

        indicatorPayloads.push({ id: nombre, name: displayName, isOscillator, isDecision, data: lineData });

        // Extra asset candlestick
        const idactivoN = Number(nombre.split(':')[0].split('_')[2]);
        if (idactivoN > 0 && idactivoN !== indicadorsIa.idnectum && !this.activoExtra.includes(idactivoN)) {
          this.activoExtra.push(idactivoN);
          // El activo extra debe venir en el mismo timeframe que la serie principal, o se
          // superpondrian velas diarias sobre un grafico intradia.
          const periodoExtra = TIMEFRAME_TO_PERIODO[this.activeTimeframe] ?? this.modeloPeriodo ?? 6;
          const API = await this._sseService.apiGetNew(`historicos-compania-idnectum?idNectum=${idactivoN}&metaapi=false&periodo=${periodoExtra}`).toPromise();
          const extraData = this.convertToLwcOhlc(API);
          this._sseService.apiPostNewSinToken('ia/obtenerNombreActivo', { idNectum: idactivoN }).subscribe({
            next: (nameData: any) => {
              if (!this.lwChart) return;
              try {
                const extra = this.lwChart.addSeries(CandlestickSeries, {
                  upColor: '#aaaaaa', downColor: '#555555',
                  borderVisible: false, wickUpColor: '#aaaaaa', wickDownColor: '#555555',
                });
                extra.setData(extraData);
                this.extraCandleSeries.set(idactivoN, extra);
              } catch {}
            },
          });
        }
      }

      // Schedule batch indicator loading
      this.addIndicatorSeriesInBatches(indicatorPayloads);

      // ── Pattern geometry ─────────────────────────────────────────────────
      const patternsByName = new Map<string, Array<{ detectionFecha: string; points: Array<{ role: string; fecha: string; price: number }> }>>();
      indicadorsIa.result.forEach((item: any) => {
        Object.keys(item).forEach((key) => {
          if (!key.startsWith('pattern_')) return;
          const sig = item[key];
          if (!sig?.points?.length) return;
          const name = key.slice('pattern_'.length);
          if (!patternsByName.has(name)) patternsByName.set(name, []);
          patternsByName.get(name)!.push({ detectionFecha: item.fecha, points: sig.points });
        });
      });
      if (patternsByName.size > 0) this.drawPatternGeometry(patternsByName);
    }

    // Apply markers
    this.applyMarkers();

    // Fecha highlight & navigate: la operación seleccionada (marco de velas y, si existe, ajustada a
    // su marcador E/S) queda marcada con 📅 y centrada.
    const fechaFocoMs = fecha ? this.ajustarAMarcadorOperacion(this.fechaOperacionAMsVela(fecha)) : null;
    if (fecha) {
      const fechaMs = fechaFocoMs;
      if (fechaMs === null) {
        console.warn('createCharts: fecha parse failed', fecha);
      } else {
        // El marcador se ancla a una vela existente: lightweight-charts no pinta marcadores en un
        // tiempo sin barra, y la hora de la operación rara vez cae justo en el inicio de una vela.
        const iVela = this.indiceVelaMasCercana(fechaMs);
        this.allMarkers.push({
          time: iVela >= 0 ? this._chartOhlcCache[iVela].time : this.toChartTime(fechaMs),
          position: 'inBar' as const, color: '#FF9800', shape: 'square' as const, text: '📅',
        });
        this.sortMarkers(this.allMarkers);
        this.applyMarkers();
      }
    }

    // Vista inicial: con `fecha` (operación seleccionada en Gestión/Contabilidad o ruta con fecha),
    // INITIAL_VIEW_BARS velas centradas en ella; sin ella, las últimas INITIAL_VIEW_BARS velas, sea
    // cual sea el periodo. Nunca fitContent() si hay velas: abriría el eje hasta el start de los
    // indicadores (años sin velas) y el visibleRangeHandler pediría ese tramo para nada.
    setTimeout(() => {
      if (!this.lwChart) return;
      try {
        const velas = this._chartOhlcCache;
        // Ningún botón de rango corresponde a "250 barras".
        if (velas.length) this.activeRange = '';
        if (fechaFocoMs !== null && velas.length) {
          this.encuadrarEnFecha(fechaFocoMs);
        } else if (velas.length) {
          const desde = velas[Math.max(0, velas.length - this.INITIAL_VIEW_BARS)].time;
          const lastMs = this.timeToMs(velas[velas.length - 1].time) ?? 0;
          const hasta = this.latestKnownMs > lastMs ? this.toChartTime(this.latestKnownMs) : velas[velas.length - 1].time;
          this.lwChart.timeScale().setVisibleRange({ from: desde, to: hasta });
        } else {
          this.lwChart.timeScale().fitContent();
        }
      } catch { try { this.lwChart.timeScale().fitContent(); } catch {} }
      // Los indicadores pueden ser más nuevos que las velas cargadas: traer las velas de ese tramo.
      try {
        const r = this.lwChart.timeScale().getVisibleLogicalRange();
        if (r) this.maybeLoadNewerChunk(r);
      } catch {}
      // Si llegó una petición de "ver período" antes de que el chart estuviera listo, aplicarla ahora.
      try { this.applyPendingFocusPeriodo(); } catch {}
    }, 100);

    // Auto-resize on container changes
    // Initialize drawing plugin (if available) and then enable options
    try { this.attachDrawingController(); } catch (e) { console.warn('attachDrawingController failed', e); }
    this.setupResizeObserver(containerEl);
  }

  // ── Pattern geometry ──────────────────────────────────────────────────────

  private readonly DSL_PATTERN_TO_GEOMETRY: Record<string, string> = {
    // ── Estructura multi-barra ────────────────────────────────────────────────
    'hch':                           'headandshoulders',
    'head_and_shoulders':            'headandshoulders',
    'hch_invertido':                 'inverseheadandshoulders',
    'inverse_head_and_shoulders':    'inverseheadandshoulders',
    'doble_techo':                   'doubletop',
    'double_top':                    'doubletop',
    'doble_suelo':                   'doublebottom',
    'double_bottom':                 'doublebottom',
    // ── Velas de 1 barra ─────────────────────────────────────────────────────
    'doji':                          'doji',
    'doji_libelula':                 'dragonflydoji',
    'dragonfly_doji':                'dragonflydoji',
    'doji_lapida':                   'gravestonedoji',
    'gravestone_doji':               'gravestonedoji',
    'martillo':                      'hammer',
    'martillo_(hammer)':             'hammer',
    'hammer':                        'hammer',
    'martillo_invertido':            'invertedhammer',
    'inverted_hammer':               'invertedhammer',
    'hombre_colgado':                'hangingman',
    'hanging_man':                   'hangingman',
    'estrella_fugaz':                'shootingstar',
    'shooting_star':                 'shootingstar',
    'marubozu_alcista':              'bullishmarubozu',
    'bullish_marubozu':              'bullishmarubozu',
    'marubozu_bajista':              'bearishmarubozu',
    'bearish_marubozu':              'bearishmarubozu',
    // ── Velas de 2 barras ────────────────────────────────────────────────────
    'envolvente_alcista':            'engulfingbullish',
    'bullish_engulfing':             'engulfingbullish',
    'engulfing_bullish':             'engulfingbullish',
    'envolvente_bajista':            'engulfingbearish',
    'bearish_engulfing':             'engulfingbearish',
    'engulfing_bearish':             'engulfingbearish',
    'harami_alcista':                'bullishharami',
    'bullish_harami':                'bullishharami',
    'harami_bajista':                'bearishharami',
    'bearish_harami':                'bearishharami',
    'harami_cruzado':                'haramicross',
    'harami_cross':                  'haramicross',
    'pinzas_(tweezer_top)':          'tweezertop',
    'tweezer_top':                   'tweezertop',
    'pinzas_(tweezer_bottom)':       'tweezerbottom',
    'tweezer_bottom':                'tweezerbottom',
    // ── Velas de 3 barras ────────────────────────────────────────────────────
    'estrella_de_la_manana':         'morningstar',
    'morning_star':                  'morningstar',
    'morningstar':                   'morningstar',
    'estrella_de_la_tarde':          'eveningstar',
    'evening_star':                  'eveningstar',
    'eveningstar':                   'eveningstar',
    'tres_soldados_blancos':         'threewhitesoldiers',
    'three_white_soldiers':          'threewhitesoldiers',
    'tres_cuervos_negros':           'threeblackcrows',
    'three_black_crows':             'threeblackcrows',
    'tres_interiores_alcistas':      'threeinsideup',
    'three_inside_up':               'threeinsideup',
    'threeinsideup':                 'threeinsideup',
    'tres_interiores_bajistas':      'threeinsidedown',
    'three_inside_down':             'threeinsidedown',
    'threeinsidedown':               'threeinsidedown',
    'tres_exteriores_alcistas':      'threeoutsideup',
    'three_outside_up':              'threeoutsideup',
    'threeoutsideup':                'threeoutsideup',
    'tres_exteriores_bajistas':      'threeoutsidedown',
    'three_outside_down':            'threeoutsidedown',
    'threeoutsidedown':              'threeoutsidedown',
  };

  // Matches: #patrones:_hch_de_60, #patrones:_martillo, #candles:_hammer_de_3
  private readonly PATRONES_DSL_REGEX = /#(?:patrones|candles?|patron):_([a-z0-9_()]+?)(?=_de_\d+|[^a-z0-9_()]|$)/gi;

  private extractPatternsFromConditions(entry: any[], exit: any[]): string[] {
    const found = new Set<string>();

    const scanText = (text: string) => {
      if (!text) return;
      this.PATRONES_DSL_REGEX.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = this.PATRONES_DSL_REGEX.exec(text.toLowerCase())) !== null) {
        const mapped = this.DSL_PATTERN_TO_GEOMETRY[m[1]];
        if (mapped) found.add(mapped);
      }
    };

    [...entry, ...exit].forEach((c) => {
      // DSL conditions store the full expression in c.dsl
      scanText(c?.dsl ?? '');
      // Legacy indicator-style conditions
      scanText(c?.indicator ?? '');
      scanText(c?.other_indicator ?? '');
    });
    return [...found];
  }

  private readonly PATTERN_COLORS: Record<string, string> = {
    doublebottom:            '#00E676',
    doubletop:               '#FF1744',
    headandshoulders:        '#FF6D00',
    inverseheadandshoulders: '#00B0FF',
    morningstar:             '#69F0AE',
    eveningstar:             '#FF4081',
    haramicross:             '#E040FB',
    bullishmarubozu:         '#00E676',
    bearishmarubozu:         '#FF1744',
    dragonflydoji:           '#FFEA00',
    gravestonedoji:          '#FF6D00',
    threeinsideup:           '#00E676',
    hammer:             '#00E676',
    invertedhammer:     '#69F0AE',
    hangingman:         '#FF1744',
    shootingstar:       '#FF4081',
    doji:               '#FFEA00',
    engulfingbullish:   '#00E676',
    engulfingbearish:   '#FF1744',
    bullishharami:      '#69F0AE',
    bearishharami:      '#FF4081',
    tweezertop:         '#FF1744',
    tweezerbottom:      '#00E676',
    threewhitesoldiers: '#00E676',
    threeblackcrows:    '#FF1744',
    threeinsidedown:         '#FF1744',
    threeoutsideup:          '#69F0AE',
    threeoutsidedown:        '#FF4081',
  };

  private readonly STRUCTURE_PATTERNS = new Set([
    'doublebottom', 'doubletop', 'headandshoulders', 'inverseheadandshoulders',
  ]);

  private readonly CANDLE_PATTERN_MARKERS: Record<string, { position: string; shape: string; text: string }> = {
    morningstar:      { position: 'belowBar', shape: 'arrowUp',   text: '☀' },
    eveningstar:      { position: 'aboveBar', shape: 'arrowDown', text: '★' },
    haramicross:      { position: 'inBar',    shape: 'circle',    text: 'H' },
    bullishmarubozu:  { position: 'belowBar', shape: 'arrowUp',   text: 'M↑' },
    bearishmarubozu:  { position: 'aboveBar', shape: 'arrowDown', text: 'M↓' },
    dragonflydoji:    { position: 'belowBar', shape: 'arrowUp',   text: 'D' },
    gravestonedoji:   { position: 'aboveBar', shape: 'arrowDown', text: 'G' },
    threeinsideup:    { position: 'belowBar', shape: 'arrowUp',   text: '3↑' },
    threeinsidedown:  { position: 'aboveBar', shape: 'arrowDown', text: '3↓' },
    threeoutsideup:   { position: 'belowBar', shape: 'arrowUp',   text: '3↑' },
    threeoutsidedown: { position: 'aboveBar', shape: 'arrowDown', text: '3↓' },
    hammer:             { position: 'belowBar', shape: 'arrowUp',   text: 'M' },
    invertedhammer:     { position: 'belowBar', shape: 'arrowUp',   text: 'Mi' },
    hangingman:         { position: 'aboveBar', shape: 'arrowDown', text: 'HC' },
    shootingstar:       { position: 'aboveBar', shape: 'arrowDown', text: 'EF' },
    doji:               { position: 'inBar',    shape: 'circle',    text: '+' },
    engulfingbullish:   { position: 'belowBar', shape: 'arrowUp',   text: 'EA' },
    engulfingbearish:   { position: 'aboveBar', shape: 'arrowDown', text: 'EB' },
    bullishharami:      { position: 'belowBar', shape: 'arrowUp',   text: 'HA' },
    bearishharami:      { position: 'aboveBar', shape: 'arrowDown', text: 'HB' },
    tweezertop:         { position: 'aboveBar', shape: 'arrowDown', text: 'PT' },
    tweezerbottom:      { position: 'belowBar', shape: 'arrowUp',   text: 'PS' },
    threewhitesoldiers: { position: 'belowBar', shape: 'arrowUp',   text: '3S' },
    threeblackcrows:    { position: 'aboveBar', shape: 'arrowDown', text: '3C' },
  };

  private drawPatternGeometry(
    patternsByName: Map<string, Array<{ detectionFecha: string; points: Array<{ role: string; fecha: string; price: number }> }>>
  ): void {
    if (!this.lwChart) return;

    patternsByName.forEach((signals, patternName) => {
      const color = this.PATTERN_COLORS[patternName] ?? '#FFFFFF';

      if (this.STRUCTURE_PATTERNS.has(patternName)) {
        // Draw connecting-line outline through key pivots
        signals.forEach((signal) => {
          if (!signal.points?.length) return;
          const sorted = [...signal.points].sort((a, b) => a.fecha.localeCompare(b.fecha));
          const seen = new Set<string>();
          const lineData = sorted
            .filter((pt) => { if (seen.has(pt.fecha)) return false; seen.add(pt.fecha); return true; })
            .map((pt) => ({ time: pt.fecha as any, value: pt.price }));
          if (lineData.length < 2) return;
          try {
            const s = this.lwChart!.addSeries(LineSeries, {
              color, lineWidth: 2, lineStyle: 0,
              priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
              title: patternName,
            });
            s.setData(lineData);
            this.patternSeriesList.push(s);
          } catch {}

          // Separate neckline for H&S patterns
          if (patternName === 'headandshoulders' || patternName === 'inverseheadandshoulders') {
            const nLeft  = signal.points.find((p) => p.role === 'neckline_left');
            const nRight = signal.points.find((p) => p.role === 'neckline_right');
            const bo     = signal.points.find((p) => p.role === 'breakout');
            if (nLeft && nRight && bo) {
              const neckData = [
                { time: nLeft.fecha as any,  value: nLeft.price },
                { time: nRight.fecha as any, value: nRight.price },
                { time: bo.fecha as any,     value: bo.price },
              ].sort((a, b) => (a.time as string).localeCompare(b.time as string));
              try {
                const ns = this.lwChart!.addSeries(LineSeries, {
                  color, lineWidth: 1, lineStyle: 1,
                  priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
                  title: 'neckline',
                });
                ns.setData(neckData);
                this.patternSeriesList.push(ns);
              } catch {}
            }
          }
        });
      } else {
        // Candlestick patterns → marker on the detection bar
        const cfg = this.CANDLE_PATTERN_MARKERS[patternName];
        if (!cfg) return;
        signals.forEach((signal) => {
          if (!signal.detectionFecha) return;
          this.allMarkers.push({
            time: signal.detectionFecha,
            position: cfg.position as any,
            color,
            shape: cfg.shape as any,
            text: cfg.text,
          });
        });
      }
    });

    this.sortMarkers(this.allMarkers);
    this.applyMarkers();
  }

  // ── Apply markers via createSeriesMarkers ─────────────────────────────────

  private applyMarkers(): void {
    if (!this.candleSeries || !this.allMarkers.length) return;
    try {
      if (this.markersPlugin) {
        this.markersPlugin.setMarkers(this.allMarkers);
      } else {
        this.markersPlugin = createSeriesMarkers(this.candleSeries, this.allMarkers);
      }
    } catch (e) { console.warn('setMarkers error', e); }
  }

  /** Time range [min,max] currently loaded in `fullPriceHistory` (candles), or null if none loaded yet. */
  private getLoadedCandleBounds(): { min: number; max: number } | null {
    if (!this.fullPriceHistory.length) return null;
    return {
      min: Number(this.fullPriceHistory[0][0]),
      max: Number(this.fullPriceHistory[this.fullPriceHistory.length - 1][0]),
    };
  }

  /**
   * Rebuilds `allMarkers` (entradas + salidas) from the current in-memory arrays and applies them.
   * Clipped to the currently loaded candle window: entradas/salidas se cargan completas desde el
   * inicio (sin paginar), pero dibujar un marcador fuera del rango de velas cargado estira el eje
   * de tiempo del gráfico hacia una zona sin datos. Se vuelven a mostrar automáticamente a medida
   * que se cargan más velas hacia atrás/adelante (Fase 4).
   */
  private rebuildEntradaSalidaMarkers(): void {
    const bounds = this.getLoadedCandleBounds();
    const inBounds = (ms: number) => !bounds || (ms >= bounds.min && ms <= bounds.max);
    const entriesForMarkers = this.entradas
      .filter((e: any) => inBounds(typeof e.time === 'number' ? e.time : Date.parse(e.time)))
      .map((e: any) => ({
        time: this.toChartTime(typeof e.time === 'number' ? e.time : Date.parse(e.time)),
        position: 'belowBar' as const,
        color: '#26a69a',
        shape: 'arrowUp' as const,
        text: 'E',
      }));
    const salidsForMarkers = this.salidas
      .filter((s: any) => inBounds(typeof s.time === 'number' ? s.time : Date.parse(s.time)))
      .map((s: any) => ({
        time: this.toChartTime(typeof s.time === 'number' ? s.time : Date.parse(s.time)),
        position: 'aboveBar' as const,
        color: '#ef5350',
        shape: 'arrowDown' as const,
        text: s.text || 'S',
      }));
    const periodoMarkers = this.embedded
      ? this.periodosActivos
          .filter((p) => inBounds(p.inicioMs) || inBounds(p.finMs))
          .flatMap((p) => ([
            { time: this.toChartTime(p.inicioMs), position: 'belowBar' as const, color: '#2962FF', shape: 'circle' as const, text: 'PI' },
            { time: this.toChartTime(p.finMs), position: 'aboveBar' as const, color: '#2962FF', shape: 'circle' as const, text: 'PF' },
          ]))
      : [];
    // console.log('[DEBUG] rebuildEntradaSalidaMarkers', {
    //   velasDesde: bounds ? new Date(bounds.min).toISOString() : null, velasHasta: bounds ? new Date(bounds.max).toISOString() : null,
    //   entradas: `${entriesForMarkers.length}/${this.entradas.length}`, salidas: `${salidsForMarkers.length}/${this.salidas.length}`,
    // });
    this.allMarkers = this.sortMarkers([...entriesForMarkers, ...salidsForMarkers, ...periodoMarkers]);
    this.applyMarkers();
  }

  // ── Trade operation connector lines (one series per trade) ───────────────

  private operLinesColor(): string {
    return this.isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
  }

  /** Devuelve el primitive enganchado a la candleSeries actual (lo recrea si la serie cambió). */
  private ensureSegmentLines(current: SegmentLinesPrimitive | null, style: SegmentLinesStyle): SegmentLinesPrimitive {
    if (current && current.attachedSeries === this.candleSeries) return current;
    if (current?.attachedSeries) { try { current.attachedSeries.detachPrimitive(current); } catch {} }
    const primitive = new SegmentLinesPrimitive(style);
    this.candleSeries.attachPrimitive(primitive);
    return primitive;
  }

  /**
   * Tiempo de la vela cargada en o justo antes de `ms` (búsqueda binaria en fullPriceHistory).
   * El primitive solo puede situar tiempos que existen en el eje; además así una E/S que no cae
   * exactamente en una vela no añade huecos al eje de tiempo.
   */
  private snapToCandleTime(ms: number): any {
    const h = this.fullPriceHistory;
    let lo = 0, hi = h.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (Number(h[mid][0]) <= ms) lo = mid; else hi = mid - 1;
    }
    return this.toChartTime(Number(h[lo][0]));
  }

  /**
   * Une entrada→salida de cada operación con un segmento propio. Antes era una LineSeries de 2
   * puntos por trade, pero con miles de operaciones esas series hacían la gráfica muy lenta; un
   * único primitive con un moveTo/lineTo por trade mantiene la garantía de que dos trades no
   * contiguos nunca quedan unidos.
   */
  private rebuildOperLineSeries(): void {
    if (!this.lwChart || !this.candleSeries || !this.fullPriceHistory.length) return;
    try {
      const bounds = this.getLoadedCandleBounds();
      const inBounds = (ms: number) => !bounds || (ms >= bounds.min && ms <= bounds.max);
      const segments: ChartSegment[] = [];
      for (let i = 0; i + 1 < this.operaciones.length; i += 2) {
        const [t1, v1] = this.operaciones[i];
        const [t2, v2] = this.operaciones[i + 1];
        if (v1 === null || v2 === null) continue;
        const t1Ms = typeof t1 === 'number' ? t1 : Date.parse(t1);
        const t2Ms = typeof t2 === 'number' ? t2 : Date.parse(t2);
        if (!inBounds(t1Ms) || !inBounds(t2Ms)) continue;
        const d1 = this.snapToCandleTime(t1Ms);
        const d2 = this.snapToCandleTime(t2Ms);
        if (d1 >= d2) continue;
        segments.push({ t1: d1, v1: Number(v1), t2: d2, v2: Number(v2) });
      }
      this.operLines = this.ensureSegmentLines(this.operLines, { color: this.operLinesColor(), lineWidth: 1, dashed: true });
      this.operLines.setSegments(segments);
    } catch (e) { console.warn('rebuildOperLineSeries error', e); }
  }

  /**
   * Une PI (inicio) con PF (fin) de cada período de "Análisis Periodos" con un segmento propio —
   * mismo criterio y misma razón que rebuildOperLineSeries().
   */
  private rebuildPeriodoLineSeries(): void {
    if (!this.lwChart || !this.candleSeries || !this.embedded || !this.fullPriceHistory.length) return;
    try {
      const bounds = this.getLoadedCandleBounds();
      const inBounds = (ms: number) => !bounds || (ms >= bounds.min && ms <= bounds.max);
      const segments: ChartSegment[] = [];
      for (const p of this.periodosActivos) {
        if (!inBounds(p.inicioMs) || !inBounds(p.finMs)) continue;
        const d1 = this.snapToCandleTime(p.inicioMs);
        const d2 = this.snapToCandleTime(p.finMs);
        if (d1 >= d2) continue;
        segments.push({ t1: d1, v1: p.valorInicio, t2: d2, v2: p.valorFin });
      }
      this.periodoLines = this.ensureSegmentLines(this.periodoLines, { color: '#2962FF', lineWidth: 2, dashed: false });
      this.periodoLines.setSegments(segments);
    } catch (e) { console.warn('rebuildPeriodoLineSeries error', e); }
  }

  // ── Chunked history loading (Fase 3/4) ────────────────────────────────────

  /** Normalizes raw OHLCV rows' timestamp (row[0]) to milliseconds (seconds→ms heuristic). */
  private normalizeHistoryRows(rows: any): number[][] {
    if (!Array.isArray(rows) || !rows.length) return [];
    try {
      return rows.map((row: any[]) => {
        const t = Number(row[0]);
        if (!Number.isFinite(t)) return row;
        const ms = t > 1e11 ? t : t > 1e9 ? t * 1000 : t; // keep ms if already ms; convert seconds->ms
        const copy = [...row];
        copy[0] = ms;
        return copy;
      });
    } catch (e) {
      console.warn('normalizeHistoryRows failed', e);
      return rows;
    }
  }

  /**
   * Mezcla dos tandas de filas OHLCV deduplicando por timestamp (row[0]) antes de ordenar.
   * lightweight-charts exige tiempos estrictamente ascendentes en setData(): si un chunk
   * pedido en el borde de `fullPriceHistory` llegara a solaparse en un punto, un duplicado
   * sin deduplicar haría que setData() tire y, como eso pasa antes de avanzar loadedFrom/
   * loadedTo, el siguiente scroll repetiría el mismo pedido y fallaría igual para siempre.
   */
  private mergeHistoryRows(a: number[][], b: number[][]): number[][] {
    const merged = new Map<number, number[]>();
    for (const row of a) merged.set(Number(row[0]), row);
    for (const row of b) merged.set(Number(row[0]), row);
    return Array.from(merged.values()).sort((x, y) => Number(x[0]) - Number(y[0]));
  }

  /**
   * Triggered on every visible-range change; fetches the previous chunk once the user scrolls near the loaded edge.
   * El borde se mide sobre la primera VELA (timeToIndex), no sobre el índice lógico 0: las series de
   * indicadores (cast_conditions) empiezan antes que las velas y el eje es la unión de todas
   * (mismo criterio que multicharts-dialog.maybeLoadOlderCandles).
   */
  private maybeLoadOlderChunk(logicalRange: { from: number; to: number }): void {
    if (this.olderChunkInFlight || this.noMoreOlderHistory) return;
    const velas = this._chartOhlcCache;
    const firstIdx = velas.length ? this.lwChart?.timeScale().timeToIndex(velas[0].time, true) ?? 0 : 0;
    // console.log('[DEBUG] maybeLoadOlderChunk', { from: logicalRange.from, firstIdx, loadedFrom: this.loadedFrom });
    if (logicalRange.from > firstIdx + 10) return;
    this.fetchOlderChunk(this.olderForceChunk ? null : this.visibleLeftMs(logicalRange));
  }

  /** lightweight-charts Time → ms (Unix seconds, 'YYYY-MM-DD' o BusinessDay). */
  private timeToMs(t: any): number | null {
    if (t == null) return null;
    if (typeof t === 'number') return t * 1000;
    if (typeof t === 'string') { const ms = Date.parse(t); return Number.isFinite(ms) ? ms : null; }
    if (typeof t === 'object' && t.year) return Date.UTC(t.year, (t.month ?? 1) - 1, t.day ?? 1);
    return null;
  }

  /**
   * ms del borde izquierdo de lo que el usuario está viendo. Si ese borde cae antes de la
   * primera vela (solo hay series de indicadores), coordinateToTime lo resuelve con la unión
   * de series; si no, se extrapola con el tamaño de barra.
   */
  private visibleLeftMs(logicalRange: { from: number; to: number }): number | null {
    if (!this.lwChart) return null;
    const ts = this.lwChart.timeScale();
    let ms = this.timeToMs(ts.coordinateToTime(0 as any));
    if (ms == null && logicalRange.from < 0) {
      // El índice lógico 0 es el primer punto de la UNIÓN de series (puede ser un indicador más
      // antiguo que las velas), no la primera vela: extrapolar desde su tiempo.
      const x0 = ts.logicalToCoordinate(0 as any);
      const firstMs = x0 != null ? this.timeToMs(ts.coordinateToTime(x0)) : null;
      if (firstMs != null) ms = firstMs + logicalRange.from * (this._isIntraday ? this.getBarMs() : this.DAY_MS);
    }
    return ms != null && Number.isFinite(ms) ? ms : null;
  }

  /**
   * Pide las velas hacia atrás. Sin `desdeObjetivo` trae un chunk contiguo (comportamiento
   * original); con él, trae de una sola vez el hueco entre lo visible y lo cargado (con un
   * margen y un tope), para que el tramo que se está mirando se pinte primero en vez de
   * descargar chunk a chunk desde el borde. Encadena hasta cubrir el borde visible.
   */
  private async fetchOlderChunk(desdeObjetivo?: number | null): Promise<void> {
    if (this.loadedFrom == null || !this._fetchCompania) { return; }
    this.olderChunkInFlight = true;
    this.beginCargaVelas();
    let seguir = false;
    try {
      const hasta = this.loadedFrom - 1;
      const chunkMs = this.getInitialWindowDays() * this.DAY_MS;
      let desde = hasta - chunkMs;
      if (desdeObjetivo != null && desdeObjetivo < desde) {
        const capMs = this._isIntraday ? chunkMs * 4 : 3650 * this.DAY_MS;
        desde = Math.max(desdeObjetivo - chunkMs / 2, hasta - capMs);
      }
      const esSalto = desde < hasta - chunkMs - 1;
      const usePeriodo = TIMEFRAME_TO_PERIODO[this.activeTimeframe] ?? this.modeloPeriodo;
      const urlHist = `historicos-compania?iso=${encodeURIComponent(this._fetchCompania)}&metaapi=${this._fetchMetaapi}&periodo=${usePeriodo}&desde=${desde}&hasta=${hasta}`;
      console.log('[DEBUG] fetchOlderChunk requesting', urlHist);
      const histRows = await this._sseService.apiGetNew(urlHist).toPromise();
      console.log('[DEBUG] fetchOlderChunk response length', histRows?.length);

      if (!histRows || !histRows.length) {
        // El backend devuelve [] también cuando la consulta falla, así que vacío no prueba que no
        // haya velas. Un salto grande vacío no se da por cargado: se reintenta con chunks normales.
        if (esSalto) {
          console.warn('[grafica] salto hacia atrás vacío, se reintenta por chunks', urlHist);
          this.olderForceChunk = true;
          seguir = true;
          return;
        }
        // Chunk normal vacío: hueco real (feriados, días sin datos) o fin del histórico.
        this.loadedFrom = desde;
        const maxVacios = this._isIntraday ? 8 : 10;
        if (++this.olderEmptyStreak >= maxVacios) this.noMoreOlderHistory = true;
        else seguir = true;
        return;
      }
      this.olderEmptyStreak = 0;
      this.olderForceChunk = false;
      this.prependOlderData(histRows);
      this.loadedFrom = desde;
      seguir = true;
    } catch (e) {
      console.warn('[DEBUG] fetchOlderChunk failed', e);
    } finally {
      this.olderChunkInFlight = false;
      this.endCargaVelas();
    }
    // El borde visible se estima (extrapolando con el tamaño de barra cuando cae antes de la primera
    // vela) y puede quedarse corto: reevaluar hasta que el tramo visible tenga velas.
    if (seguir) this.recheckVisibleCoverage();
  }

  /**
   * Tras cargar un chunk, vuelve a comprobar con el rango visible ya actualizado si aún faltan velas
   * a la izquierda o a la derecha. Los eventos de rango emitidos durante la carga se ignoraron
   * (chunk en vuelo), así que sin esto la carga se detendría con el hueco a medias.
   * maybeLoad*Chunk no hace nada si ya está cubierto, y noMore*History corta el bucle.
   */
  private recheckVisibleCoverage(): void {
    setTimeout(() => {
      if (!this.lwChart) return;
      const r = this.lwChart.timeScale().getVisibleLogicalRange();
      if (!r) return;
      this.maybeLoadOlderChunk(r);
      this.maybeLoadNewerChunk(r);
    }, 50);
  }

  /**
   * Prepends an older chunk of candles without disrupting the current viewport.
   * Entradas/salidas se cargan completas desde el inicio en fetchData() (no se vuelven
   * a pedir acá), pero los marcadores/conectores de operaciones están recortados a la ventana de
   * velas cargada (ver `rebuildEntradaSalidaMarkers`/`rebuildOperLineSeries`) — hay que
   * reconstruirlos para que las operaciones que caen en este chunk pasen a verse.
   */
  private prependOlderData(histRows: number[][]): void {
    if (!this.lwChart || !this.candleSeries || !histRows.length) return;
    if (this.historyMutationLock) { setTimeout(() => this.prependOlderData(histRows), 260); return; }
    this.historyMutationLock = true;
    try {
      const currentRange = this.lwChart.timeScale().getVisibleRange();

      const normalized = this.normalizeHistoryRows(histRows);
      this.fullPriceHistory = this.mergeHistoryRows(normalized, this.fullPriceHistory);
      this.fullVolumeHistory = this.fullPriceHistory.map((r: any[]) => [r[0], r[r.length - 1]] as [number, number | null]);
      this._chartOhlcCache = this.convertToLwcOhlc(this.fullPriceHistory);
      const c = this.getChartColors();
      this.candleSeries.setData(this._chartOhlcCache);
      if (this.volumeSeries) this.volumeSeries.setData(this.convertToLwcVolume(this.fullPriceHistory, c.volUp, c.volDown));
      this.rebuildEntradaSalidaMarkers();
      this.rebuildOperLineSeries();
      this.rebuildPeriodoLineSeries();

      if (currentRange) {
        try { this.lwChart.timeScale().setVisibleRange(currentRange); } catch {}
      }
    } finally {
      this.historyMutationLock = false;
    }
  }

  /** Triggered on every visible-range change; fetches the next chunk once the user scrolls near the loaded right edge. */
  private maybeLoadNewerChunk(logicalRange: { from: number; to: number }): void {
    const lastIndex = this.fullPriceHistory.length - 1;
    console.log('[DEBUG] maybeLoadNewerChunk', { to: logicalRange.to, lastIndex, newerChunkInFlight: this.newerChunkInFlight, noMoreNewerHistory: this.noMoreNewerHistory, loadedTo: this.loadedTo, now: Date.now() });
    if (this.newerChunkInFlight || this.noMoreNewerHistory) return;
    if (lastIndex < 0 || logicalRange.to < lastIndex - 10) return;
    console.log('[DEBUG] maybeLoadNewerChunk -> calling fetchNewerChunk');
    this.fetchNewerChunk();
  }

  private async fetchNewerChunk(): Promise<void> {
    if (this.loadedTo == null || !this._fetchCompania) { console.log('[DEBUG] fetchNewerChunk early-return', { loadedTo: this.loadedTo, fetchCompania: this._fetchCompania }); return; }
    // Techo de lo que puede existir: el reloj del navegador no es fiable (puede ir por detrás de los
    // datos que devuelve el backend), así que también cuenta la fecha más nueva vista en indicadores/ticks.
    // +1 día: las fechas de los indicadores se parsean en hora local y las velas vienen en hora de
    // mercado como UTC, así que pueden diferir unas horas.
    const techoMs = Math.max(Date.now(), this.latestKnownMs + this.DAY_MS);
    if (this.loadedTo >= techoMs) { console.log('[DEBUG] fetchNewerChunk: loadedTo >= techo, marking noMoreNewerHistory'); this.noMoreNewerHistory = true; return; }
    this.newerChunkInFlight = true;
    this.beginCargaVelas();
    let seguir = false;
    try {
      const desde = this.loadedTo + 1;
      const chunkMs = this.getInitialWindowDays() * this.DAY_MS;
      // Si el usuario mira un tramo más nuevo que lo cargado, traer de una vez hasta ahí (con tope).
      const visRight = this.visibleRightMs();
      let hasta = desde + chunkMs;
      if (!this.newerForceChunk && visRight != null && visRight > hasta) {
        const capMs = this._isIntraday ? chunkMs * 4 : 3650 * this.DAY_MS;
        hasta = Math.min(visRight + chunkMs / 2, desde + capMs);
      }
      hasta = Math.min(techoMs, hasta);
      const esSalto = hasta > desde + chunkMs + 1;
      const usePeriodo = TIMEFRAME_TO_PERIODO[this.activeTimeframe] ?? this.modeloPeriodo;
      const urlHist = `historicos-compania?iso=${encodeURIComponent(this._fetchCompania)}&metaapi=${this._fetchMetaapi}&periodo=${usePeriodo}&desde=${desde}&hasta=${hasta}`;
      console.log('[DEBUG] fetchNewerChunk requesting', urlHist);
      const histRows = await this._sseService.apiGetNew(urlHist).toPromise();
      console.log('[DEBUG] fetchNewerChunk response length', histRows?.length);

      if (!histRows || !histRows.length) {
        // Igual que en fetchOlderChunk: un salto vacío se reintenta por chunks; un chunk vacío es
        // un hueco y se avanza, cortando al llegar al techo o tras varios vacíos seguidos.
        if (esSalto) {
          console.warn('[grafica] salto hacia adelante vacío, se reintenta por chunks', urlHist);
          this.newerForceChunk = true;
          seguir = true;
          return;
        }
        this.loadedTo = hasta;
        if (hasta >= techoMs || ++this.newerEmptyStreak >= 8) this.noMoreNewerHistory = true;
        else seguir = true;
        return;
      }
      this.newerEmptyStreak = 0;
      this.newerForceChunk = false;
      this.appendNewerData(histRows);
      this.loadedTo = hasta;
      seguir = true;
    } catch (e) {
      console.warn('[DEBUG] fetchNewerChunk failed', e);
    } finally {
      this.newerChunkInFlight = false;
      this.endCargaVelas();
    }
    // Reevaluar la cobertura con el rango ya actualizado: los eventos de rango que provocó el
    // append llegaron con el chunk en vuelo y se ignoraron, y el siguiente no tiene por qué llegar.
    if (seguir) this.recheckVisibleCoverage();
  }

  /** ms del borde derecho de lo visible (por la unión de series, incluidos los indicadores más nuevos que las velas). */
  private visibleRightMs(): number | null {
    if (!this.lwChart) return null;
    const ts = this.lwChart.timeScale();
    const w = ts.width();
    const ms = w ? this.timeToMs(ts.coordinateToTime((w - 1) as any)) : null;
    return ms != null && Number.isFinite(ms) ? ms : null;
  }

  /**
   * Appends a newer chunk of candles without disrupting the current viewport.
   * Reconstruye marcadores/conectores de operaciones por la misma razón que `prependOlderData`.
   */
  private appendNewerData(histRows: number[][]): void {
    if (!this.lwChart || !this.candleSeries || !histRows.length) return;
    if (this.historyMutationLock) { setTimeout(() => this.appendNewerData(histRows), 260); return; }
    this.historyMutationLock = true;
    try {
      const currentRange = this.lwChart.timeScale().getVisibleRange();

      const normalized = this.normalizeHistoryRows(histRows);
      this.fullPriceHistory = this.mergeHistoryRows(this.fullPriceHistory, normalized);
      this.fullVolumeHistory = this.fullPriceHistory.map((r: any[]) => [r[0], r[r.length - 1]] as [number, number | null]);
      this._chartOhlcCache = this.convertToLwcOhlc(this.fullPriceHistory);
      const c = this.getChartColors();
      this.candleSeries.setData(this._chartOhlcCache);
      if (this.volumeSeries) this.volumeSeries.setData(this.convertToLwcVolume(this.fullPriceHistory, c.volUp, c.volDown));
      this.rebuildEntradaSalidaMarkers();
      this.rebuildOperLineSeries();
      this.rebuildPeriodoLineSeries();

      if (currentRange) {
        try { this.lwChart.timeScale().setVisibleRange(currentRange); } catch {}
      }
    } finally {
      this.historyMutationLock = false;
    }
  }

  // ── Progressive indicator loading ─────────────────────────────────────────

  private addIndicatorSeriesInBatches(
    payloads: Array<{ id: string; name: string; isOscillator: boolean; isDecision: boolean; data: any[] }>
  ): void {
    const work = () => {
      if (!this.lwChart) return;
      const batch = payloads.splice(0, this.INDICATOR_BATCH_SIZE);
      batch.forEach((p) => {
        if (p.isDecision) return; // decision flags handled via markers
        if (!p.data.length) return;
        try {
          const color = this.colores[this.colorIndex % this.colores.length];
          this.colorIndex++;
          let series: any;
          if (p.isOscillator) {
            // Create oscillator pane on demand
            if (!this.oscillatorPane) {
              this.oscillatorPane = (this.lwChart as any).addPane();
            }
            series = this.oscillatorPane.addSeries(LineSeries, {
              color,
              lineWidth: 1,
              priceLineVisible: false,
              lastValueVisible: false,
              crosshairMarkerVisible: true,
            });
          } else {
            series = this.lwChart!.addSeries(LineSeries, {
              color,
              lineWidth: 1,
              priceLineVisible: false,
              lastValueVisible: false,
              crosshairMarkerVisible: true,
              priceFormat: { type: 'price', precision: 5, minMove: 0.00001 },
            });
          }
          series.setData(p.data);
          if (!p.isOscillator) {
            // Un operando fuera de magnitud (p.ej. la constante 100 de `#price:_close < 100`)
            // no debe aplastar las velas; si es constante se señala en el borde del panel.
            fitOverlayToPriceScale(
              series,
              p.data.map((d: any) => d.value),
              this.fullPriceHistory.map((r: any[]) => Number(r[3])),
              this.fullPriceHistory.map((r: any[]) => Number(r[2])),
              color,
              (v) => this.formatLegendValue(v),
            );
          }
          this.indicatorSeriesMap.set(p.id, series);
          const lastValue = this.formatLegendValue(p.data[p.data.length - 1]?.value);
          this.legendItems.push({ id: p.id, name: p.name, color, value: lastValue, lastValue });
        } catch (e) { console.warn('Error adding indicator series', p.id, e); }
      });

      if (payloads.length > 0) {
        setTimeout(work, this.INDICATOR_BATCH_DELAY);
      } else {
        // Los indicadores empiezan antes que las velas y amplían el eje de tiempo; el plugin de
        // marcadores solo reindexa cuando cambian los datos de su serie, así que sin esto las E/S
        // quedan con índices viejos fuera de la vista hasta el próximo setMarkers.
        this.applyMarkers();
      }
    };
    setTimeout(work, this.INDICATOR_BATCH_DELAY);
  }

  // ── Theme update ──────────────────────────────────────────────────────────

  updateChartTheme(): void {
    if (!this.lwChart) return;
    const c = this.getChartColors();

    this.lwChart.applyOptions({
      layout: {
        background: { type: ColorType.Solid, color: c.background },
        textColor: c.text,
      },
      grid: {
        vertLines: { color: c.grid },
        horzLines: { color: c.grid },
      },
      rightPriceScale: { borderColor: c.border },
      timeScale: { borderColor: c.border },
    });

    if (this.candleSeries) {
      if (this.chartType === 'candlestick' || this.chartType === 'heikin-ashi') {
        this.candleSeries.applyOptions({
          upColor: c.upColor, downColor: c.downColor,
          wickUpColor: c.upColor, wickDownColor: c.downColor,
        });
      } else if (this.chartType === 'bar') {
        this.candleSeries.applyOptions({ upColor: c.upColor, downColor: c.downColor });
      } else if (this.chartType === 'line') {
        this.candleSeries.applyOptions({ color: c.upColor });
      } else if (this.chartType === 'area') {
        this.candleSeries.applyOptions({
          lineColor: c.upColor, topColor: c.upColor + '66', bottomColor: c.upColor + '11',
        });
      }
    }

    if (this.volumeSeries) {
      // Re-apply volume data with updated colors
      const volData = this.convertToLwcVolume(this.fullPriceHistory, c.volUp, c.volDown);
      if (volData.length) {
        this.volumeSeries.setData(volData);
        this.lastVolumeTime = volData[volData.length - 1].time;
      }
    }

    // Update operation connector line color
    this.operLines?.setColor(this.operLinesColor());
  }

  // ── Real-time WebSocket candlestick update ────────────────────────────────

  async updateLastCandlestick(data: any) {
  if (!this.lwChart || !this.candleSeries || !data) return;
  if (!this.fullPriceHistory || this.fullPriceHistory.length === 0) return;

  const getMs = (t: any): number | null => {
    if (!t) return null;
    const n = typeof t === 'number' ? t : Date.parse(t);
    return isFinite(n) ? (n < 1e12 ? n * 1000 : n) : null;
  };

  const ts = getMs(data.timestamp) ?? getMs(data.fecha) ?? Date.now();
  const chartTime = this.toChartTime(ts);

  const open  = parseFloat(data.open);
  const high  = parseFloat(data.maximo  ?? data.high);
  const low   = parseFloat(data.minimo  ?? data.low);
  const close = parseFloat(data.close);
  const volume = parseFloat(data.volumen ?? data.volume ?? '0') || 0;

  if ([open, high, low, close].some(v => !Number.isFinite(v))) {
    console.warn('OHLC inválido, ignorando tick:', { open, high, low, close });
    return;
  }


  // ── Sincronizar fullPriceHistory ────────────────────────────────────────
  const idx = this.fullPriceHistory.findIndex(r => Number(r[0]) === ts);
  const row = [ts, open, high, low, close, volume];
  if (idx >= 0) {
    this.fullPriceHistory[idx] = row;
  } else {
    this.fullPriceHistory.push(row);
    this.fullPriceHistory.sort((a, b) => Number(a[0]) - Number(b[0]));
  }

  // ── Actualizar vela principal ───────────────────────────────────────────
  try {
    if (['line', 'area', 'baseline'].includes(this.chartType)) {
      this.candleSeries.update({ time: chartTime, value: close });
    } else if (this.chartType === 'heikin-ashi') {
      // Para Heikin-Ashi recalcular solo las últimas 2 velas es suficiente
      const ohlc = this.convertToLwcOhlc(this.fullPriceHistory.slice(-2));
      const ha = this.computeHeikinAshi(ohlc);
      if (ha.length) this.candleSeries.update(ha[ha.length - 1]);
    } else {
      this.candleSeries.update({ time: chartTime, open, high, low, close });
    }
  } catch (e: any) {
    // lightweight-charts lanza error si el tiempo no es monotónico (tiempo anterior al último)
    // En ese caso reconstruimos la serie completa
    console.warn('candleSeries.update() falló, reconstruyendo serie:', e?.message ?? e);
    try {
      const ohlcData = this.convertToLwcOhlc(this.fullPriceHistory);
      if (this.chartType === 'heikin-ashi') {
        this.candleSeries.setData(this.computeHeikinAshi(ohlcData));
      } else if (['line', 'area', 'baseline'].includes(this.chartType)) {
        this.candleSeries.setData(ohlcData.map((d: any) => ({ time: d.time, value: d.close })));
      } else {
        this.candleSeries.setData(ohlcData);
      }
    } catch (e2) {
      console.error('setData() fallback también falló:', e2);
    }
    return;
  }

  // ── Actualizar volumen ──────────────────────────────────────────────────
  if (this.volumeSeries) {
    const c = this.getChartColors();
    const prevClose = this.fullPriceHistory.length > 1
      ? Number(this.fullPriceHistory[this.fullPriceHistory.length - 2][4])
      : close;
    const volColor = close >= prevClose ? c.volUp + '88' : c.volDown + '88';
    try {
      this.volumeSeries.update({ time: chartTime, value: volume, color: volColor });
      this.lastVolumeTime = chartTime;
    } catch {
      // Si falla el update de volumen, reconstruir silenciosamente
      try {
        const volData = this.convertToLwcVolume(this.fullPriceHistory, c.volUp, c.volDown);
        if (volData.length) {
          this.volumeSeries.setData(volData);
          this.lastVolumeTime = volData[volData.length - 1].time;
        }
      } catch {}
    }
  }

  // ── Recalcular indicadores IA (debounce largo) ──────────────────────────
  try {
    this.scheduleCastConditions();
  } catch {}
}

  // ── Misc helpers ──────────────────────────────────────────────────────────

  onSlideToggleChange(event: any) {
    this.expandir = !this.expandir;
  }

  filterChartByDate(selectedDate: Date) {
    if (!this.lwChart) return;
    const dateStr = selectedDate.toISOString().slice(0, 10);
    try {
      this.lwChart.timeScale().setVisibleRange({ from: dateStr as any, to: dateStr as any });
    } catch {}
  }

  resetPriceZoom() {
    if (!this.lwChart) return;
    try { this.lwChart.timeScale().fitContent(); } catch {}
  }

  autoScalePrices() {
    if (!this.lwChart) return;
    try { this.lwChart.timeScale().fitContent(); } catch {}
  }

  /**
   * Fuerza un recálculo de tamaño del chart. Necesario cuando el gráfico se crea (o recibe
   * datos) mientras su contenedor está oculto (p. ej. la pestaña "Gráfica Análisis" no es la
   * pestaña activa dentro de Models): lightweight-charts no se autoajusta al tamaño real hasta
   * que alguien se lo pida explícitamente. Llamar al activar la pestaña que lo contiene.
   */
  public refreshLayout(): void {
    this.syncEmbeddedContainerHeight();
    if (!this.lwChart) return;
    const containerEl = this.containerRef?.nativeElement ?? null;
    if (!containerEl) return;
    const rect = containerEl.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      try {
        // Solo redimensionar: el encuadre lo fija la vista inicial de createCharts. Un fitContent()
        // aquí pisaba esa vista según quién terminara antes (llega por scheduleEnsureCorrectSize al
        // cambiar de activo, por window:resize y al cambiar de pestaña) y abría el eje a años de indicador.
        this.lwChart.resize(rect.width, rect.height);
      } catch {}
    }
  }

  /**
   * .container-fluid no puede fijar su altura con un offset de píxeles adivinado en CSS (el
   * chrome superior varía: header de Models, header global de la app en /grafica-modelo) ni con
   * height:100% (la cadena de porcentajes de Angular Material puede resolver a 0 mientras la
   * pestaña "Gráfica Análisis" no está activa/adjunta al DOM). En vez de eso, medimos acá la
   * posición real del contenedor y fijamos su altura explícitamente — así no depende de ningún
   * ancestro intermedio. Aplica tanto en modo embebido como en la ruta /grafica-modelo.
   */
  private syncEmbeddedContainerHeight(): void {
    const el = this.containerFluidRef?.nativeElement;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    // Descartamos mediciones fuera de rango (layout aún no asentado) en vez de aplicarlas — ver
    // el mismo guard en models.component.ts syncContainerHeight() para el bug que motivó esto.
    if (top > 0 && top < window.innerHeight) {
      el.style.height = `calc(100dvh - ${Math.round(top)}px)`;
    }
  }

  private ensureSizeRetryTimer: any = null;

  /**
   * Reintenta refreshLayout() hasta que el contenedor tenga un tamaño real (>0). Necesario porque
   * mientras la pestaña "Gráfica Análisis" no está activa, su contenido queda "detached" del DOM
   * (ver @ViewChild containerRef) y no tiene tamaño — un solo intento a un tiempo fijo puede caer
   * antes de que la pestaña realmente se muestre. Se llama al cargar un activo (por si el usuario
   * ya está en la pestaña) y al cambiar de pestaña en ModelsComponent (por si el dato llegó antes).
   */
  public scheduleEnsureCorrectSize(attemptsLeft: number = 12): void {
    if (this.ensureSizeRetryTimer) clearTimeout(this.ensureSizeRetryTimer);
    this.ensureSizeRetryTimer = setTimeout(() => {
      this.ensureSizeRetryTimer = null;
      this.syncEmbeddedContainerHeight();
      const containerEl = this.containerRef?.nativeElement ?? null;
      const rect = containerEl?.getBoundingClientRect();
      if (this.lwChart && rect && rect.width > 0 && rect.height > 0) {
        this.refreshLayout();
        return;
      }
      if (attemptsLeft > 0) this.scheduleEnsureCorrectSize(attemptsLeft - 1);
    }, 250);
  }

  // ── ResizeObserver ────────────────────────────────────────────────────────

  private setupResizeObserver(el: HTMLElement): void {
    if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null; }
    this.resizeObserver = new ResizeObserver((entries) => {
      if (!this.lwChart || !el) return;
      const entry = entries[0];
      const w = entry?.contentRect?.width  || el.getBoundingClientRect().width  || el.clientWidth;
      const h = entry?.contentRect?.height || el.getBoundingClientRect().height || el.clientHeight;
      if (w > 0 && h > 0) {
        try { this.lwChart.resize(w, h); } catch {}
      }
    });
    this.resizeObserver.observe(el);
  }

  // ── Destroy ───────────────────────────────────────────────────────────────

  private destroyChart(): void {
    if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null; }
    this.drawingCtl.dispose();
    if (this.lwChart) {
      if (this.crosshairHandler) {
        try { this.lwChart.unsubscribeCrosshairMove(this.crosshairHandler); } catch {}
        this.crosshairHandler = null;
      }
      if (this.visibleRangeHandler) {
        try { this.lwChart.timeScale().unsubscribeVisibleLogicalRangeChange(this.visibleRangeHandler); } catch {}
        this.visibleRangeHandler = null;
      }
      try { this.lwChart.remove(); } catch {}
      this.lwChart = null;
    }
    this.candleSeries = null;
    this.volumeSeries = null;
    this.operLines = null;
    this.periodoLines = null;
    this.markersPlugin = null;
    this.openTradePriceLines = [];
    this.oscillatorPane = null;
    this.indicatorSeriesMap.clear();
    this.extraCandleSeries.clear();
    this.patternSeriesList = [];
    this.colorIndex = 0;
    this.groupedIndicators = {};
    this.allMarkers = [];
    this.legendItems = [];
    this._chartOhlcCache = [];
    this.crosshairOhlc = null;
    this.hoveredPeriodoLegend = null;
    // NOTA: loadedFrom/loadedTo/noMore*History/*ChunkInFlight/historyMutationLock NO se
    // resetean acá — createCharts() llama a destroyChart() al empezar, pero loadedFrom/
    // loadedTo ya fueron seteados por fetchData() ANTES de llamar a createCharts(); si se
    // resetean acá quedan en null para siempre y el scroll-paginado (Fase 4) nunca dispara
    // ninguna request. El reset real de este estado vive al principio de fetchData().
  }

  /** Deja la gráfica embebida vacía (sin activo ni modelo): velas, E/S, indicadores y socket fuera. */
  private limpiarGrafica(): void {
    this.subscription?.unsubscribe();
    this.companiaWebsocketService.disconnect();
    this._rangeRequestSeq++;
    this._fetchCompania = '';
    this.loadedFrom = null;
    this.loadedTo = null;
    this.resetDescripcionState();
    this.destroyChart();
    this.idmodelo = 0;
    // En Models no hay combo: no conservar un modelo viejo. En Portfolio Labs el combo sigue mandando.
    if (!this.modelosLista.length) this.selectedModeloId = null;
    this.isModelo = false;
    this.nombreCompania = '';
    this.selectedCompanyId = null;
    this.condicionEntradaLista = [];
    this.condicionSalidaLista = [];
  }

  ngOnDestroy(): void {
    this.subIdModeloActivo?.unsubscribe();
    this.subDarktema?.unsubscribe();
    this.subscription?.unsubscribe();
    this.subAnalisisCompania?.unsubscribe();
    this.subPeriodosCompania?.unsubscribe();
    this.subPeriodosEnriquecidos?.unsubscribe();
    this.subPeriodoEnfocar?.unsubscribe();
    this.subModeloSeleccionadoGrafica?.unsubscribe();
    this.subIdMercado?.unsubscribe();
    if (this._focusPeriodoTimer) { clearTimeout(this._focusPeriodoTimer); this._focusPeriodoTimer = null; }
    if (this.ensureSizeRetryTimer) { clearTimeout(this.ensureSizeRetryTimer); this.ensureSizeRetryTimer = null; }
    this.companiaWebsocketService.disconnect();
    if (this.pendingHistoryTimer) { clearTimeout(this.pendingHistoryTimer); this.pendingHistoryTimer = null; }
    this.clearMarketCountdown();
    this.destroyChart();
  }
} 